import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import * as ort from 'onnxruntime-web';
import { MP3_LOOKUP } from '../geezData';
import { registerMp3Lookup, getCharAudioB64 } from '../utils/audioUtils';
import {
  SPACE_UNIT_SECONDS,
  PERIOD_PAUSE_UNITS,
  countPauseUnits,
  getPauseDuration,
  getPauseUnits,
  isAudioPause,
} from '../utils/textSpacing';
import './AudioPanel.css';

if (typeof window !== 'undefined') {
  registerMp3Lookup(MP3_LOOKUP);

  // Configure CPU Multi-threading for ONNX WebAssembly
  const numCPUThreads = Math.min(navigator.hardwareConcurrency || 4, 20);
  ort.env.wasm.numThreads = numCPUThreads;

  const isProduction = process.env.NODE_ENV === 'production';
  const CDN_BASE = 'https://assets.fidelkeys.app';
  if (isProduction) {
    // Production: Use CDN for ONNX wasm files and model assets
    ort.env.wasm.wasmPaths = `${CDN_BASE}/onnx-wasm/`;
  }
  console.log(`Configuring WebAssembly to run on ${numCPUThreads} CPU threads.`);
}

// --- Audio Buffer to WAV Converter (Supports Mono/Stereo) ---
function audioBufferToWav(buffer) {
  const numOfChan = buffer.numberOfChannels,
        length = buffer.length * numOfChan * 2,
        bufferLength = 44 + length,
        view = new DataView(new ArrayBuffer(bufferLength)),
        channels = [],
        sampleRate = buffer.sampleRate;
  
  let i, sample, offset = 0, pos = 0;
  
  const writeString = (str) => {
    for (let j = 0; j < str.length; j++) {
      view.setUint8(pos + j, str.charCodeAt(j));
    }
    pos += str.length;
  };

  writeString('RIFF');                                     // RIFF identifier
  view.setUint32(pos, 36 + length, true); pos += 4;         // file length
  writeString('WAVE');                                     // RIFF type
  writeString('fmt ');                                     // format chunk identifier
  view.setUint32(pos, 16, true); pos += 4;                 // format chunk length
  view.setUint16(pos, 1, true); pos += 2;                  // sample format (raw)
  view.setUint16(pos, numOfChan, true); pos += 2;          // channel count
  view.setUint32(pos, sampleRate, true); pos += 4;         // sample rate
  view.setUint32(pos, sampleRate * numOfChan * 2, true); pos += 4; // byte rate
  view.setUint16(pos, numOfChan * 2, true); pos += 2;      // block align
  view.setUint16(pos, 16, true); pos += 2;                 // bits per sample
  writeString('data');                                     // data chunk identifier
  view.setUint32(pos, length, true); pos += 4;             // data chunk length

  for (i = 0; i < numOfChan; i++) {
    channels.push(buffer.getChannelData(i));
  }

  while (pos < bufferLength) {
    for (i = 0; i < numOfChan; i++) {
      sample = Math.max(-1, Math.min(1, channels[i][offset]));
      sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(pos, sample, true);
      pos += 2;
    }
    offset++;
  }

  return new Blob([view], { type: 'audio/wav' });
}

function createOfflineFallbackTone(offlineCtx, clip, duration, playRate) {
  const charCode = clip.charCode || 65;
  const freq = 200 + (charCode % 200);
  const sr = offlineCtx.sampleRate;
  const buf = offlineCtx.createBuffer(2, sr * duration, sr);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < d.length; i++) {
      d[i] = Math.sin(2 * Math.PI * freq * i / sr) * 0.3;
    }
  }
  return buf;
}

// --- ONNX Helpers ---
function transliterateGeez(text) {
  const BASE_CONSONANTS = [
    "ሀ","ለ","ሐ","መ","ሠ","ረ","ሰ","ሸ","ቀ","በ","ተ","ቸ","ኀ","ነ","ኘ","አ","ከ","ኸ","ወ","ዐ","ዘ","ዠ","የ","ደ","ጀ","ገ","ጠ","ጨ","ጰ","ጸ","ፀ","ፈ","ፐ","ቨ","ቈ","","ኰ","ዀ","ጐ"
  ];

  const LATIN_MAPPING = [
    "h",  "l",  "h",  "m",  "s",  "r",  "s",  "sh", 
    "q",  "b",  "t",  "ch", "h",  "n",  "ny", "a", 
    "k",  "h",  "w",  "a",  "z",  "zh", "y",  "d", 
    "j",  "g",  "t",  "c",  "p",  "ts", "ts", "f",  "p",  "v",
    "qw", "hw", "kw", "hw", "gw"
  ];

  const VOWELS = ["a", "u", "i", "aa", "ee", "e", "o", "wa"];
  
  let result = "";
  for (let char of text) {
    let code = char.charCodeAt(0);
    
    if (code >= 0x1200 && code <= 0x137C) {
      let col = code % 8;
      let baseCode = code - col;
      let baseChar = String.fromCharCode(baseCode);
      
      let rowIndex = BASE_CONSONANTS.indexOf(baseChar);
      
      if (rowIndex !== -1) {
        let consonant = LATIN_MAPPING[rowIndex];
        let vowel = VOWELS[col];
        
        if (consonant === "a") {
          result += (vowel === "") ? "i" : vowel;
        } else {
          result += consonant + vowel;
        }
      }
    } else if (char === "፣") {
      result += ", ";
    } else if (char === "።") {
      result += ". ";
    } else if (char === "፤") {
      result += "; ";
    } else if (char === "፡") {
      result += " ";
    } else {
      result += char;
    }
  }
  
  return result.toLowerCase().trim();
}

function bufferToWav(buffer, sampleRate) {
  let length = buffer.length * 2;
  let view = new DataView(new ArrayBuffer(44 + length));
  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + length, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // Linear PCM
  view.setUint16(22, 1, true); // Mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, length, true);

  let offset = 44;
  for (let i = 0; i < buffer.length; i++, offset += 2) {
    let s = Math.max(-1, Math.min(1, buffer[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
  }
  return new Blob([view], { type: 'audio/wav' });
}

// --- Timeline DAW Helpers ---
function resolve1DLayout(clips, boundaryMin = 0) {
  const items = clips.map(c => ({
    id: c.id,
    isStatic: c.isStatic,
    duration: c.duration,
    startTime: c.targetStart,
    targetStart: c.targetStart,
    mobility: c.isStatic ? 1.0 : 0.05,
  }));

  const maxIterations = 80;
  for (let iter = 0; iter < maxIterations; iter++) {
    items.sort((a, b) => a.startTime - b.startTime);
    let hasOverlap = false;

    for (let i = 0; i < items.length - 1; i++) {
      const a = items[i];
      const b = items[i + 1];
      const endA = a.startTime + a.duration;
      if (endA - b.startTime > 0.0001) {
        hasOverlap = true;
        const overlap = endA - b.startTime;
        const totalMobility = a.mobility + b.mobility;
        if (totalMobility > 0) {
          a.startTime -= overlap * (a.mobility / totalMobility);
          b.startTime += overlap * (b.mobility / totalMobility);
        }
      }
    }

    let clampOverlap = false;
    items.sort((a, b) => a.startTime - b.startTime);
    if (items.length > 0 && items[0].startTime < boundaryMin) {
      items[0].startTime = boundaryMin;
      clampOverlap = true;
      for (let i = 0; i < items.length - 1; i++) {
        const a = items[i];
        const b = items[i + 1];
        const endA = a.startTime + a.duration;
        if (endA - b.startTime > 0.0001) {
          b.startTime = endA;
        }
      }
    }

    if (!hasOverlap && !clampOverlap) {
      break;
    }
  }

  return items;
}

function resolveTrackLayout(staticClips, draggedClips) {
  const combined = [
    ...staticClips.map(c => ({ ...c, isStatic: true, targetStart: c.startTime })),
    ...draggedClips.map(c => ({ ...c, isStatic: false, targetStart: c.startTime }))
  ];

  const resolved = resolve1DLayout(combined);

  return resolved.map(item => {
    const originalClip = combined.find(c => c.id === item.id);
    return {
      ...originalClip,
      startTime: item.startTime
    };
  });
}

const getRawIndex = (str, nonPauseIdx) => {
  if (nonPauseIdx < 0) return 0;
  let count = 0;
  for (let i = 0; i < str.length; i++) {
    if (!isAudioPause(str[i])) {
      if (count === nonPauseIdx) return i;
      count++;
    }
  }
  return str.length;
};

function getCrossfadeGainAtTime(clip, T, allClips) {
  let gain = 1.0;
  for (const other of allClips) {
    if (other.id === clip.id) continue;
    const oStart = other.startTime;
    const oEnd = other.startTime + other.duration;
    const cStart = clip.startTime;
    const cEnd = clip.startTime + clip.duration;

    const overlapStart = Math.max(cStart, oStart);
    const overlapEnd = Math.min(cEnd, oEnd);

    if (overlapEnd > overlapStart) {
      const isOut = cStart < oStart || (cStart === oStart && clip.id < other.id);
      if (isOut) {
        if (T > overlapEnd) gain *= 0.0;
        else if (T >= overlapStart) gain *= (1.0 - (T - overlapStart) / (overlapEnd - overlapStart));
      } else {
        if (T < overlapStart) gain *= 0.0;
        else if (T <= overlapEnd) gain *= ((T - overlapStart) / (overlapEnd - overlapStart));
      }
    }
  }
  return gain;
}

const INITIAL_TRACK_COUNT = 3;
const DEFAULT_CLIP_DURATION = 0.35;
const CONTROL_PANEL_WIDTH = 140;
const ZOOM_LEVELS = [25, 50, 75, 100, 150, 200, 300, 400, 600, 800, 1000];
const MIN_CLIP_WIDTH = 20;
const CLIP_EDGE_PADDING = 7;

function ToolbarGroup({ label, children, className = '' }) {
  return (
    <div className={`toolbar-group ${className}`} role="group" aria-label={label}>
      <span className="toolbar-group-label">{label}</span>
      <div className="toolbar-group-controls">{children}</div>
    </div>
  );
}

const DEFAULT_ENVELOPE = [
  { time: 0, gain: 1 },
  { time: 1, gain: 1 },
];

const CLIP_COLOR_THEME = {
  className: 'clip-track-0',
  bg: 'var(--clip-accent-bg)',
  border: 'var(--clip-accent-border)',
  waveform: 'var(--clip-accent-waveform)',
};

let decodeCtx = null;
const durationCache = new Map();
const waveformCache = new Map();

function getAudioContext() {
  if (typeof window === 'undefined') return null;
  if (!decodeCtx) {
    // Utilizing the official W3C AudioSession API. By default, iOS Safari sets
    // the audioSession type to 'ambient', which respects the hardware ringer switch.
    // Setting it to 'playback' forces the OS to treat Web Audio API sounds as primary
    // media playback, allowing it to bypass the physical silent switch.
    if (typeof navigator !== 'undefined' && 'audioSession' in navigator) {
      try {
        navigator.audioSession.type = 'playback';
      } catch (e) {
        console.warn('Failed to configure W3C AudioSession playback type:', e);
      }
    }

    decodeCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return decodeCtx;
}

function b64ToArrayBuffer(b64) {
  if (!b64) return new ArrayBuffer(0);
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

async function extractWaveformData(b64, points = 150) {
  if (!b64 || typeof window === 'undefined') return null;
  const ctx = getAudioContext();
  if (!ctx) return null;

  const key = b64.slice(0, 40) + b64.slice(-40);
  if (waveformCache.has(key)) return waveformCache.get(key);

  try {
    const arrayBuf = b64ToArrayBuffer(b64);
    const audioBuffer = await ctx.decodeAudioData(arrayBuf);
    const chan0 = audioBuffer.getChannelData(0);
    const hasChan1 = audioBuffer.numberOfChannels > 1;
    const chan1 = hasChan1 ? audioBuffer.getChannelData(1) : null;
    const totalSamples = audioBuffer.length;

    const data = [];
    const bucketSize = Math.max(1, Math.floor(totalSamples / points));
    for (let i = 0; i < points; i++) {
      const start = i * bucketSize;
      const end = Math.min(start + bucketSize, totalSamples);
      let sumSq = 0;
      for (let j = start; j < end; j++) {
        let val = chan0[j];
        if (hasChan1) val = (val + chan1[j]) / 2;
        sumSq += val * val;
      }
      data.push(Math.sqrt(sumSq / (end - start)));
    }
    waveformCache.set(key, data);
    return data;
  } catch (e) {
    console.warn('Failed to extract waveform data', e);
    return null;
  }
}

async function getMp3Duration(b64) {
  if (!b64 || typeof window === 'undefined') return DEFAULT_CLIP_DURATION;
  const ctx = getAudioContext();
  if (!ctx) return DEFAULT_CLIP_DURATION;

  const key = b64.slice(-40);
  if (durationCache.has(key)) return durationCache.get(key);

  try {
    const arrayBuf = b64ToArrayBuffer(b64);
    const buf = await ctx.decodeAudioData(arrayBuf);
    durationCache.set(key, buf.duration);
    return buf.duration;
  } catch (e) {
    console.warn('Failed to decode MP3 duration', e);
    return DEFAULT_CLIP_DURATION;
  }
}

function generateWaveformData(duration, seed) {
  const points = 150;
  const data = [];
  for (let i = 0; i < points; i++) {
    const t = i / points;
    const baseAmp = Math.sin(t * Math.PI) * (0.3 + 0.5 * Math.abs(Math.sin(t * (3 + seed * 0.5) + seed) * Math.cos(t * (7 + seed * 0.3))));
    data.push(Math.max(0.02, Math.min(0.95, baseAmp + (Math.random() - 0.5) * 0.05)));
  }
  return data;
}

function formatTime(seconds) {
  if (seconds < 0) seconds = 0;
  if (seconds < 60) return `${seconds.toFixed(2)}s`;
  return `${Math.floor(seconds / 60)}:${(seconds % 60).toFixed(2).padStart(5, '0')}`;
}

function getEffectiveTrackVolume(track, anySoloed, masterVolume) {
  if (track.muted) return 0;
  if (anySoloed && !track.soloed) return 0;
  return track.volume * masterVolume;
}

function reconstructText(clips, originalText = '') {
  const sortedClips = [...clips].sort((a, b) => a.startTime - b.startTime);
  if (!sortedClips.length) return originalText || '';

  const clipsText = sortedClips.map(c => c.char).join('');
  const textNonPause = [...originalText].filter(ch => !isAudioPause(ch)).join('');
  let reconstructed = '';

  function getGapString(numUnits, originalPauses) {
    if (numUnits <= 0) return '';
    const isPeriod = numUnits >= PERIOD_PAUSE_UNITS;
    if (isPeriod) {
      if (originalPauses && originalPauses.includes('.')) return '.';
      return '።';
    } else {
      if (originalPauses && originalPauses.includes(' ')) return ' ';
      return '፡';
    }
  }

  if (clipsText === textNonPause && textNonPause.length > 0) {
    const nonPauseIndices = [];
    for (let i = 0; i < originalText.length; i++) {
      if (!isAudioPause(originalText[i])) nonPauseIndices.push(i);
    }

    for (let i = 0; i < sortedClips.length; i++) {
      const c = sortedClips[i];
      if (i === 0) {
        const leading = originalText.substring(0, nonPauseIndices[0]);
        if (c.startTime >= 0.065) {
          const expectedUnits = Math.round(c.startTime / SPACE_UNIT_SECONDS);
          if (expectedUnits > 0) {
            reconstructed += getGapString(expectedUnits, leading);
          }
        }
      } else {
        const prevClip = sortedClips[i - 1];
        const gap = c.startTime - (prevClip.startTime + prevClip.duration);
        if (gap >= 0.065) {
          const expectedUnits = Math.round(gap / SPACE_UNIT_SECONDS);
          if (expectedUnits > 0) {
            const originalPauseText = originalText.substring(nonPauseIndices[i - 1] + 1, nonPauseIndices[i]);
            reconstructed += getGapString(expectedUnits, originalPauseText);
          }
        }
      }
      reconstructed += c.char;
    }
    const lastNonPauseIdx = nonPauseIndices[nonPauseIndices.length - 1];
    const trailingPauseText = originalText.substring(lastNonPauseIdx === undefined ? 0 : lastNonPauseIdx + 1);
    if (trailingPauseText && [...trailingPauseText].every(isAudioPause)) {
      const expectedUnits = countPauseUnits(trailingPauseText);
      reconstructed += getGapString(expectedUnits, trailingPauseText);
    }
  } else {
    const clipToTextIdx = [];
    let textIdx = 0;
    for (let i = 0; i < sortedClips.length; i++) {
      const char = sortedClips[i].char;
      while (textIdx < originalText.length && (isAudioPause(originalText[textIdx]) || originalText[textIdx] !== char)) {
        textIdx++;
      }
      if (textIdx < originalText.length) {
        clipToTextIdx[i] = textIdx;
        textIdx++;
      } else {
        clipToTextIdx[i] = -1;
      }
    }

    for (let i = 0; i < sortedClips.length; i++) {
      const c = sortedClips[i];
      if (i === 0) {
        if (c.startTime >= 0.065) {
          const numUnits = Math.round(c.startTime / SPACE_UNIT_SECONDS);
          if (numUnits > 0) {
            const idxCurr = clipToTextIdx[i];
            const leading = idxCurr !== -1 ? originalText.substring(0, idxCurr) : '';
            reconstructed += getGapString(numUnits, leading);
          }
        }
      } else {
        const prevClip = sortedClips[i - 1];
        const gap = c.startTime - (prevClip.startTime + prevClip.duration);
        if (gap >= 0.065) {
          const numUnits = Math.round(gap / SPACE_UNIT_SECONDS);
          if (numUnits > 0) {
            const idxPrev = clipToTextIdx[i - 1];
            const idxCurr = clipToTextIdx[i];
            const originalPauses = (idxPrev !== -1 && idxCurr !== -1 && idxCurr > idxPrev + 1)
              ? originalText.substring(idxPrev + 1, idxCurr)
              : '';
            reconstructed += getGapString(numUnits, originalPauses);
          }
        }
      }
      reconstructed += c.char;
    }

    const lastMatchedIdx = clipToTextIdx[clipToTextIdx.length - 1];
    if (lastMatchedIdx !== undefined && lastMatchedIdx !== -1) {
      const trailing = originalText.substring(lastMatchedIdx + 1);
      if ([...trailing].every(isAudioPause)) {
        const expectedUnits = countPauseUnits(trailing);
        reconstructed += getGapString(expectedUnits, trailing);
      }
    }
  }
  return reconstructed || originalText;
}

function makeEnvelope(fadeType) {
  switch (fadeType) {
    case 'in': return [{ time: 0, gain: 0 }, { time: 1, gain: 1 }];
    case 'out': return [{ time: 0, gain: 1 }, { time: 1, gain: 0 }];
    case 'inout': return [{ time: 0, gain: 0 }, { time: 0.15, gain: 1 }, { time: 0.85, gain: 1 }, { time: 1, gain: 0 }];
    case 'smooth': return [{ time: 0, gain: 0.55 }, { time: 0.12, gain: 1 }, { time: 0.88, gain: 1 }, { time: 1, gain: 0.55 }];
    case 'ejective': return [{ time: 0, gain: 0.55 }, { time: 0.01, gain: 1 }, { time: 1, gain: 1 }];
    case 'sustain': return [{ time: 0, gain: 0.85 }, { time: 0.05, gain: 1 }, { time: 0.9, gain: 1 }, { time: 1, gain: 0.85 }];
    case 'bridge': return [{ time: 0, gain: 0.35 }, { time: 0.2, gain: 1 }, { time: 0.8, gain: 1 }, { time: 1, gain: 0.35 }];
    default: return [...DEFAULT_ENVELOPE];
  }
}

function gainAtTime(envelope, t) {
  if (!envelope || envelope.length === 0) return 1;
  if (t <= envelope[0].time) return envelope[0].gain;
  if (t >= envelope[envelope.length - 1].time) return envelope[envelope.length - 1].gain;
  for (let i = 0; i < envelope.length - 1; i++) {
    const a = envelope[i], b = envelope[i + 1];
    if (t >= a.time && t <= b.time) {
      return a.gain + ((t - a.time) / (b.time - a.time)) * (b.gain - a.gain);
    }
  }
  return 1;
}

function getEffectiveGainAtTime(clip, T, allClips, envelope, crossfadeMode) {
  if (!clip.duration) return 0;
  const baseGain = gainAtTime(envelope, (T - clip.startTime) / clip.duration);
  const xfadeGain = crossfadeMode === 'automatic' ? getCrossfadeGainAtTime(clip, T, allClips) : 1;
  const finalGain = baseGain * xfadeGain;
  return isNaN(finalGain) ? 0 : finalGain;
}

function isDefaultEnvelope(envelope) {
  const env = envelope || DEFAULT_ENVELOPE;
  return env.length === DEFAULT_ENVELOPE.length && env.every((wp, i) =>
    Math.abs(wp.time - DEFAULT_ENVELOPE[i].time) < 1e-6 &&
    Math.abs(wp.gain - DEFAULT_ENVELOPE[i].gain) < 1e-6
  );
}

function buildAutoCrossfadeEnvelope(clip, allClips) {
  if (!clip.duration) return [...DEFAULT_ENVELOPE];
  const transitionTimes = new Set([0, 1]);
  const clipStart = clip.startTime;
  const clipEnd = clipStart + clip.duration;

  for (const other of allClips) {
    if (other.id === clip.id) continue;
    const overlapStart = Math.max(clipStart, other.startTime);
    const overlapEnd = Math.min(clipEnd, other.startTime + other.duration);
    if (overlapEnd <= overlapStart) continue;

    if (overlapStart >= clipStart && overlapStart <= clipEnd) {
      transitionTimes.add((overlapStart - clipStart) / clip.duration);
    }
    if (overlapEnd >= clipStart && overlapEnd <= clipEnd) {
      transitionTimes.add((overlapEnd - clipStart) / clip.duration);
    }
  }

  const times = Array.from(transitionTimes)
    .filter(t => Number.isFinite(t))
    .map(t => Math.max(0, Math.min(1, t)))
    .sort((a, b) => a - b);

  const envelope = [];
  for (const time of times) {
    const last = envelope[envelope.length - 1];
    const gain = Math.max(0, Math.min(1, getCrossfadeGainAtTime(clip, clipStart + time * clip.duration, allClips)));
    if (last && Math.abs(last.time - time) < 1e-6) last.gain = gain;
    else envelope.push({ time, gain });
  }
  return envelope.length >= 2 ? envelope : [...DEFAULT_ENVELOPE];
}

function playFallbackTone(ctx, clip, duration, playRate, destNode, startTime) {
  const charCode = clip.charCode || 65;
  const freq = 200 + (charCode % 200);
  const sr = ctx.sampleRate;
  const buf = ctx.createBuffer(2, sr * duration, sr);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < d.length; i++) d[i] = Math.sin(2 * Math.PI * freq * i / sr) * 0.3;
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = playRate;
  src.connect(destNode);
  src.start(startTime);
  src.stop(startTime + duration);
  return src;
}

export default function AudioPanel({ text, onTextChange, compact = false, onGetState, sharedState, onRestoreComplete }) {
  const [audioMode, setAudioMode] = useState('timeline');

  // ONNX Runtime Web local state
  const [onnxInitialized, setOnnxInitialized] = useState(false);
  const [onnxFailed, setOnnxFailed] = useState(false);
  const [onnxStatus, setOnnxStatus] = useState("Initializing ONNX Runtime...");
  const [onnxStatusColor, setOnnxStatusColor] = useState("var(--text-muted)");
  const [onnxProgress, setOnnxProgress] = useState(0);
  const [onnxLoading, setOnnxLoading] = useState(false);
  const [onnxGenerating, setOnnxGenerating] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);

  const onnxSessionRef = useRef(null);
  const onnxVocabRef = useRef(null);
  const onnxAudioPlayerRef = useRef(null);

  const [tracks, setTracks] = useState(() =>
    Array.from({ length: INITIAL_TRACK_COUNT }, (_, i) => ({
      id: `track-${i + 1}`,
      label: `Track ${i + 1}`,
      clips: [],
      volume: 0.7,
      muted: false,
      soloed: false,
    }))
  );
  
  const totalClips = useMemo(() => tracks.reduce((s, t) => s + t.clips.length, 0), [tracks]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [status, setStatus] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('male');
  const [masterVolume, setMasterVolume] = useState(0.8);
  const [zoom, setZoom] = useState(100);
  const [playheadPos, setPlayheadPos] = useState(0);
  const [snapEnabled, setSnapEnabled] = useState(false);
  const [crossfadeMode, setCrossfadeMode] = useState('automatic');
  const [hoveredClipId, setHoveredClipId] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  
  const [selectedClipIds, setSelectedClipIds] = useState(() => new Set());
  const [draggingClipIds, setDraggingClipIds] = useState(null);

  // Client-side Mobile Device Recognition
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const checkMobile = () => {
      setIsMobile(
        window.matchMedia('(max-width: 768px)').matches ||
        ('ontouchstart' in window) ||
        navigator.maxTouchPoints > 0
      );
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const audioCtxRef = useRef(null);
  const masterGainRef = useRef(null);
  const masterLimiterRef = useRef(null);
  const trackGainRefsRef = useRef({});
  const sourceNodesRef = useRef({});
  const playheadAnimRef = useRef(null);
  const playStartTimeRef = useRef(0);
  const prevTextRef = useRef('');
  const genTimeoutRef = useRef(null);
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const workspaceScrollRef = useRef(null);
  const workspaceRef = useRef(null);
  const trackDomRefs = useRef({});
  const syncTimeoutRef = useRef(null);
  const isInternalSyncRef = useRef(false);
  const clipStartCorrectionRef = useRef(new Map());

  // Tracks the timeline position (in seconds) where playback started,
  // allowing us to calculate relative progress and offset context time.
  const playStartTimeOffsetRef = useRef(0);

  const clearUndoRedo = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    setCanUndo(false);
    setCanRedo(false);
  }, []);

  const tracksRef = useRef(tracks);
  useEffect(() => { tracksRef.current = tracks; }, [tracks]);

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const fetchWithProgress = async (url, label) => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${url} (Status: ${response.status})`);
    }
    const contentLength = +response.headers.get('Content-Length');
    if (!contentLength) {
      return await response.arrayBuffer();
    }
    
    setOnnxProgress(0);
    const reader = response.body.getReader();
    let receivedLength = 0;
    let chunks = [];
    
    while (true) {
      const {done, value} = await reader.read();
      if (done) break;
      chunks.push(value);
      receivedLength += value.length;
      
      const pct = Math.round((receivedLength / contentLength) * 100);
      const loadedMB = (receivedLength / (1024 * 1024)).toFixed(1);
      const totalMB = (contentLength / (1024 * 1024)).toFixed(1);
      
      setOnnxProgress(pct);
      setOnnxStatus(`Downloading ${label}: ${loadedMB}MB / ${totalMB}MB`);
    }
    
    let chunksAll = new Uint8Array(receivedLength);
    let position = 0;
    for(let chunk of chunks) {
      chunksAll.set(chunk, position);
      position += chunk.length;
    }
    return chunksAll.buffer;
  };

  const initOnnx = useCallback(async () => {
    if (onnxInitialized || onnxLoading || onnxFailed) return;
    setOnnxLoading(true);
    setOnnxFailed(false);
    setOnnxStatusColor("var(--text-secondary)");
    try {
      const isProduction = process.env.NODE_ENV === 'production';
      const CDN_BASE = 'https://assets.fidelkeys.app';
      const vocabUrl = isProduction ? `${CDN_BASE}/ml/tts/vocab.json` : 'ml/tts/vocab.json';
      const modelUrl = isProduction ? `${CDN_BASE}/ml/tts/model.onnx` : 'ml/tts/model.onnx';

      setOnnxStatus("Loading vocabulary (vocab.json)...");
      const vocabRes = await fetch(vocabUrl);
      if (!vocabRes.ok) throw new Error("Failed to load vocab.json. Make sure vocab.json is in your public/ folder.");
      const vocabData = await vocabRes.json();
      onnxVocabRef.current = vocabData;

      setOnnxStatus("Fetching model.onnx with progress tracking...");
      const modelBuffer = await fetchWithProgress(modelUrl, "model.onnx");

      setOnnxStatus("Compiling ONNX model inside WebAssembly (takes 3-5 seconds)...");
      setOnnxProgress(100);
      
      await new Promise(r => setTimeout(r, 100));

      const sessionOptions = {
        executionProviders: ["wasm"],
        graphOptimizationLevel: "all"
      };

      const session = await ort.InferenceSession.create(modelBuffer, sessionOptions);
      onnxSessionRef.current = session;

      setOnnxStatus("Ready!");
      setOnnxStatusColor("var(--accent)");
      setOnnxInitialized(true);
    } catch (err) {
      console.error(err);
      setOnnxStatusColor("var(--danger)");
      setOnnxStatus(`Initialization Error: ${err.message}. Ensure model.onnx and vocab.json are inside your public/ folder.`);
      setOnnxFailed(true);
    } finally {
      setOnnxLoading(false);
    }
  }, [onnxInitialized, onnxLoading, onnxFailed]);

  useEffect(() => {
    if (audioMode === 'onnx' && !onnxInitialized && !onnxLoading && !onnxFailed) {
      initOnnx();
    }
  }, [audioMode, onnxInitialized, onnxLoading, onnxFailed, initOnnx]);

  const handleGenerateOnnxSpeech = useCallback(async () => {
    const rawText = text ? text.trim() : "";
    if (!rawText) {
      setOnnxStatusColor("var(--warning)");
      setOnnxStatus("Please write or draw some Ge'ez text first.");
      return;
    }
    if (!onnxSessionRef.current || !onnxVocabRef.current) {
      setOnnxStatusColor("var(--danger)");
      setOnnxStatus("ONNX session or vocabulary details missing.");
      return;
    }

    setOnnxGenerating(true);
    setOnnxStatusColor("var(--text-secondary)");
    setOnnxProgress(0);
    setOnnxStatus("Romanizing and cleaning text...");

    let generationProgress = 0;
    const progressInterval = setInterval(() => {
      if (generationProgress < 95) {
        generationProgress += (98 - generationProgress) * 0.12;
        setOnnxProgress(Math.round(generationProgress));
      }
    }, 120);

    try {
      await new Promise(r => setTimeout(r, 50));

      const romanized = transliterateGeez(rawText);
      const cleaned = romanized.toLowerCase().replace(/[^a-z ]/g, "");
      console.log("Cleaned Roman Text sent to Model:", cleaned);

      const vocab = onnxVocabRef.current;
      const rawIds = [];
      for (let char of cleaned) {
        if (vocab[char] !== undefined) {
          rawIds.push(vocab[char]);
        }
      }

      const inputIds = [0];
      for (let id of rawIds) {
        inputIds.push(id);
        inputIds.push(0);
      }

      setOnnxStatus("Preparing ONNX input tensors...");
      const seqLen = inputIds.length;
      const inputIdsData = BigInt64Array.from(inputIds.map(BigInt));
      const attentionMaskData = BigInt64Array.from({ length: seqLen }, () => 1n);

      const inputIdsTensor = new ort.Tensor('int64', inputIdsData, [1, seqLen]);
      const attentionMaskTensor = new ort.Tensor('int64', attentionMaskData, [1, seqLen]);

      setOnnxStatus(`Running ONNX Inference using CPU threads...`);
      await new Promise(r => setTimeout(r, 20));

      const feeds = {
        input_ids: inputIdsTensor,
        attention_mask: attentionMaskTensor
      };

      const results = await onnxSessionRef.current.run(feeds);
      
      setOnnxStatus("Encoding floating-point audio to WAV format...");
      const outputNames = Object.keys(results);
      const waveformName = outputNames.includes("waveform") ? "waveform" : outputNames[0];
      const float32Audio = results[waveformName].data;

      const wavBlob = bufferToWav(float32Audio, 16000);
      const url = URL.createObjectURL(wavBlob);
      setAudioUrl(url);

      clearInterval(progressInterval);
      setOnnxProgress(100);
      setOnnxStatus("Synthesis complete!");
      
      setTimeout(() => {
        if (onnxAudioPlayerRef.current) {
          onnxAudioPlayerRef.current.play().catch(e => console.warn("Autoplay blocked:", e));
        }
      }, 100);

    } catch (err) {
      console.error(err);
      clearInterval(progressInterval);
      setOnnxStatusColor("var(--danger)");
      setOnnxStatus(`Inference failed: ${err.message}`);
    } finally {
      setOnnxGenerating(false);
    }
  }, [text]);

  const handleModeToggle = (mode) => {
    stopAll();
    setAudioMode(mode);
    if (mode === 'onnx' && onnxFailed) {
      setOnnxFailed(false);
    }
  };

  useEffect(() => {
    let isActive = true;
    const changeVoice = async () => {
      const currentTracks = tracksRef.current;
      if (!currentTracks.some(t => t.clips.length > 0)) return;
      
      const allClips = currentTracks.flatMap(t => t.clips.map(c => ({ ...c, _trackId: t.id })));
      allClips.sort((a, b) => a.startTime - b.startTime);
      if (allClips.length === 0) return;
      
      const updatedClips = [];
      const lastEndByTrack = new Map();
      let cumulativeShift = 0;
      
      for (let i = 0; i < allClips.length; i++) {
        if (!isActive) return;
        const clip = allClips[i];
        const b64 = getCharAudioB64(clip.char, selectedVoice);
        const newDur = await getMp3Duration(b64);
        
        const previousCorrection = clipStartCorrectionRef.current.get(clip.id) || 0;
        let newStartTime = (clip.startTime - previousCorrection) + cumulativeShift;
        const previousSameTrackEnd = lastEndByTrack.get(clip._trackId);
        
        if (previousSameTrackEnd !== undefined) {
          const minStartTime = previousSameTrackEnd + 0.02;
          if (newStartTime < minStartTime) {
            cumulativeShift += minStartTime - newStartTime;
            clipStartCorrectionRef.current.set(clip.id, minStartTime - newStartTime);
            newStartTime = minStartTime;
          } else {
            clipStartCorrectionRef.current.delete(clip.id);
          }
        } else {
          clipStartCorrectionRef.current.delete(clip.id);
        }
        
        updatedClips.push({
          ...clip,
          b64,
          duration: newDur,
          originalDuration: newDur,
          startTime: Math.max(0, newStartTime),
        });
        
        lastEndByTrack.set(clip._trackId, newStartTime + newDur);
        cumulativeShift += (newDur - clip.duration);
      }
      
      if (isActive) {
        const newTracks = currentTracks.map(t => ({
          ...t,
          clips: updatedClips
            .filter(c => c._trackId === t.id)
            .map(({ _trackId, ...clip }) => clip)
            .sort((a, b) => a.startTime - b.startTime),
        }));
        setTracks(newTracks);
        setStatus(`Voice changed to ${selectedVoice}`);
      }
    };
    changeVoice();
    return () => { isActive = false; };
  }, [selectedVoice]);

  const allClipsGlobal = useMemo(() => tracks.flatMap(t => t.clips), [tracks]);

  useEffect(() => {
    const ctx = getAudioContext();
    if (ctx && !masterGainRef.current) {
      masterGainRef.current = ctx.createGain();
      masterLimiterRef.current = ctx.createDynamicsCompressor();
      masterLimiterRef.current.threshold.value = -1;
      masterLimiterRef.current.knee.value = 0;
      masterLimiterRef.current.ratio.value = 20;
      masterLimiterRef.current.attack.value = 0.001;
      masterLimiterRef.current.release.value = 0.05;
      masterGainRef.current.connect(masterLimiterRef.current);
      masterLimiterRef.current.connect(ctx.destination);
    }
    return () => { stopAll(); };
  }, []);

  const syncTextToParent = useCallback((tracksSnapshot) => {
    const allClips = tracksSnapshot.flatMap(t => t.clips).sort((a, b) => a.startTime - b.startTime);
    const reconstructedText = reconstructText(allClips, text);

    if (reconstructedText !== text && onTextChange) {
      isInternalSyncRef.current = true;
      onTextChange(reconstructedText);
      setStatus(`Reordered: "${reconstructedText}"`);
    }
  }, [text, onTextChange]);

  const queueTextSync = useCallback((tracksSnapshot) => {
    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    syncTimeoutRef.current = setTimeout(() => syncTextToParent(tracksSnapshot), 150);
  }, [syncTextToParent]);

  const textFromTracks = useCallback((tracksSnapshot) => {
    return reconstructText(tracksSnapshot.flatMap(t => t.clips), text);
  }, [text]);

  const generateClipsFromText = useCallback(async (textInput, voice = selectedVoice) => {
    if (!textInput) return [];
    const clips = [];
    let currentTime = 0;
    for (const ch of [...textInput]) {
      if (isAudioPause(ch)) {
        currentTime += getPauseDuration(ch);
        continue;
      }
      const b64 = getCharAudioB64(ch, voice);
      const actualDuration = await getMp3Duration(b64);
      const realWaveform = b64 ? await extractWaveformData(b64) : null;
      clips.push({
        id: `clip-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        char: ch,
        charCode: ch.charCodeAt(0),
        startTime: currentTime,
        duration: actualDuration,
        originalDuration: actualDuration,
        trackId: `track-${(clips.length % tracksRef.current.length) + 1}`,
        b64,
        waveform: realWaveform || generateWaveformData(actualDuration, ch.charCodeAt(0)),
        envelope: [...DEFAULT_ENVELOPE],
      });
      currentTime += actualDuration;
    }
    return clips;
  }, [selectedVoice]);

  const getState = useCallback(() => ({
    tracks: tracks.map(t => ({
      ...t,
      clips: t.clips.map(c => ({
        id: c.id,
        char: c.char,
        charCode: c.charCode,
        startTime: c.startTime,
        duration: c.duration,
        originalDuration: c.originalDuration,
        trackId: c.trackId,
        envelope: c.envelope,
      }))
    })),
    selectedVoice,
    masterVolume,
    zoom,
    snapEnabled,
    crossfadeMode,
  }), [tracks, selectedVoice, masterVolume, zoom, snapEnabled, crossfadeMode]);

  useEffect(() => {
    if (onGetState) onGetState(getState);
  }, [onGetState, getState]);

  useEffect(() => {
    if (sharedState) {
      const restore = async () => {
        setIsRestoring(true);
        setSelectedVoice(sharedState.selectedVoice || 'male');
        setMasterVolume(sharedState.masterVolume !== undefined ? sharedState.masterVolume : 0.8);
        setZoom(sharedState.zoom || 100);
        setSnapEnabled(!!sharedState.snapEnabled);
        setCrossfadeMode(sharedState.crossfadeMode === 'manual' ? 'manual' : 'automatic');
        
        if (sharedState.tracks) {
          const restoredTracks = await Promise.all(sharedState.tracks.map(async t => ({
            ...t,
            clips: await Promise.all((t.clips || []).map(async c => {
              const b64 = getCharAudioB64(c.char, sharedState.selectedVoice || 'male');
              const waveform = await extractWaveformData(b64);
              return { ...c, b64, waveform };
            }))
          })));
          
          setTracks(restoredTracks);
          clearUndoRedo();
          setSelectedClipIds(new Set());
          
          const restoredText = sharedState.text || reconstructText(restoredTracks.flatMap(t => t.clips), text);
          prevTextRef.current = restoredText;
          if (onRestoreComplete) onRestoreComplete(restoredText);
        }
        setIsRestoring(false);
      };
      restore();
    }
  }, [sharedState, clearUndoRedo]);

  useEffect(() => {
    let active = true;
    if (isRestoring) return;

    if (isInternalSyncRef.current) {
      isInternalSyncRef.current = false;
      prevTextRef.current = text;
      return;
    }
    if (text !== prevTextRef.current) {
      clearUndoRedo();
      clipStartCorrectionRef.current.clear();
    }
    if (!text) {
      prevTextRef.current = '';
      setTracks(prev => prev.map(t => ({ ...t, clips: [] })));
      return;
    }
    if (genTimeoutRef.current) clearTimeout(genTimeoutRef.current);
    genTimeoutRef.current = setTimeout(async () => {
      if (!active) return;
      const currentText = text;
      const prev = prevTextRef.current;
      prevTextRef.current = currentText;

      if (prev) {
        const getNonPauseIndices = (str) => {
          const indices = [];
          for (let i = 0; i < str.length; i++) {
            if (!isAudioPause(str[i])) indices.push(i);
          }
          return indices;
        };
        const countPauseUnitsBetween = (str, start, end) => countPauseUnits(str.substring(start, end));

        const nonPauseIndicesPrev = getNonPauseIndices(prev);
        const nonPauseIndicesCurrent = getNonPauseIndices(currentText);
        const oldNonPause = nonPauseIndicesPrev.map(i => prev[i]).join('');
        const newNonPause = nonPauseIndicesCurrent.map(i => currentText[i]).join('');

        if (oldNonPause === newNonPause) {
          const n = nonPauseIndicesPrev.length;
          const diffPauseUnits = new Array(n).fill(0);
          for (let i = 0; i < n; i++) {
            const pauseUnitsPrev = i === 0
              ? countPauseUnitsBetween(prev, 0, nonPauseIndicesPrev[i])
              : countPauseUnitsBetween(prev, nonPauseIndicesPrev[i - 1] + 1, nonPauseIndicesPrev[i]);
            const pauseUnitsCurrent = i === 0
              ? countPauseUnitsBetween(currentText, 0, nonPauseIndicesCurrent[i])
              : countPauseUnitsBetween(currentText, nonPauseIndicesCurrent[i - 1] + 1, nonPauseIndicesCurrent[i]);
            diffPauseUnits[i] = pauseUnitsCurrent - pauseUnitsPrev;
          }

          setTracks(prevTracks => {
            if (!active) return prevTracks;
            const allClips = prevTracks.flatMap(t => t.clips).sort((a, b) => a.startTime - b.startTime);

            let firstGapIndex = diffPauseUnits.findIndex(v => v > 0);

            if (firstGapIndex === -1) {
              let firstRemoveIndex = diffPauseUnits.findIndex(v => v < 0);
              if (firstRemoveIndex === -1) {
                queueTextSync(prevTracks);
                return prevTracks;
              }
              
              let cumulativeShift = diffPauseUnits.slice(0, firstRemoveIndex + 1).reduce((sum, v) => sum + v * SPACE_UNIT_SECONDS, 0);
              let shiftDelta = cumulativeShift;
              if (shiftDelta < 0 && firstRemoveIndex > 0 && firstRemoveIndex < allClips.length) {
                const maxShift = (allClips[firstRemoveIndex - 1].startTime + allClips[firstRemoveIndex - 1].duration) - allClips[firstRemoveIndex].startTime;
                if (shiftDelta < maxShift) shiftDelta = maxShift;
              }
              
              const shiftedIds = new Set(allClips.slice(firstRemoveIndex).map(c => c.id));
              const updated = prevTracks.map(t => ({
                ...t,
                clips: t.clips.map(c => shiftedIds.has(c.id) ? { ...c, startTime: Math.max(0, c.startTime + shiftDelta) } : c)
              }));
              queueTextSync(updated);
              return updated;
            }

            const gapAmount = diffPauseUnits[firstGapIndex] * SPACE_UNIT_SECONDS;
            let shiftDelta = 0;
            if (firstGapIndex > 0 && firstGapIndex < allClips.length) {
              shiftDelta = (allClips[firstGapIndex - 1].startTime + allClips[firstGapIndex - 1].duration + gapAmount) - allClips[firstGapIndex].startTime;
            } else if (firstGapIndex === 0 && allClips.length > 0) {
              shiftDelta = gapAmount - allClips[0].startTime;
            }

            if (Math.abs(shiftDelta) < 0.001) {
              const clipShifts = {};
              let cumulativeShift = 0;
              for (let i = 0; i < allClips.length; i++) {
                if (i < diffPauseUnits.length) cumulativeShift += diffPauseUnits[i] * SPACE_UNIT_SECONDS;
                clipShifts[allClips[i].id] = cumulativeShift;
              }

              const updated = prevTracks.map(t => ({
                ...t,
                clips: t.clips.map(c => clipShifts[c.id] ? { ...c, startTime: Math.max(0, c.startTime + clipShifts[c.id]) } : c)
              }));
              queueTextSync(updated);
              return updated;
            }

            const shiftedIds = new Set(allClips.slice(firstGapIndex).map(c => c.id));
            const updated = prevTracks.map(t => ({
              ...t,
              clips: t.clips.map(c => shiftedIds.has(c.id) ? { ...c, startTime: Math.max(0, c.startTime + shiftDelta) } : c)
            }));

            queueTextSync(updated);
            return updated;
          });

          setStatus('Gaps adjusted');
          return;
        }
      }

      if (prev && !currentText.startsWith(prev)) {
        const oldChars = [...prev].filter(ch => !isAudioPause(ch));
        const newChars = [...currentText].filter(ch => !isAudioPause(ch));

        let prefixLen = 0;
        while (prefixLen < oldChars.length && prefixLen < newChars.length && oldChars[prefixLen] === newChars[prefixLen]) {
          prefixLen++;
        }
        let suffixLen = 0;
        while (suffixLen < (oldChars.length - prefixLen) && suffixLen < (newChars.length - prefixLen) &&
               oldChars[oldChars.length - 1 - suffixLen] === newChars[newChars.length - 1 - suffixLen]) {
          suffixLen++;
        }

        const insertedChars = newChars.slice(prefixLen, newChars.length - suffixLen);
        const replaceCount = oldChars.length - prefixLen - suffixLen;

        const currentTracks = tracksRef.current;
        const allClips = currentTracks.flatMap(t => t.clips).sort((a, b) => a.startTime - b.startTime);

        const insertIdx = prefixLen;

        let insertStartTime = 0;
        const rawPrefixEndIdx = getRawIndex(currentText, prefixLen);
        const leadingRaw = currentText.substring(0, rawPrefixEndIdx);
        let lastNonPauseInLeading = -1;
        for (let i = leadingRaw.length - 1; i >= 0; i--) {
          if (!isAudioPause(leadingRaw[i])) {
            lastNonPauseInLeading = i;
            break;
          }
        }
        const trailingPausesInLeading = leadingRaw.substring(lastNonPauseInLeading + 1);
        const pauseUnitsAfterPrefix = countPauseUnits(trailingPausesInLeading);

        if (insertIdx > 0 && allClips[insertIdx - 1]) {
          const prevClip = allClips[insertIdx - 1];
          insertStartTime = (prevClip.startTime + prevClip.duration) + pauseUnitsAfterPrefix * SPACE_UNIT_SECONDS;
        } else {
          insertStartTime = pauseUnitsAfterPrefix * SPACE_UNIT_SECONDS;
        }

        const newClipsToInsert = [];
        let currentNewTime = insertStartTime;
        for (const ch of insertedChars) {
          const b64 = getCharAudioB64(ch, selectedVoice);
          const dur = await getMp3Duration(b64);
          if (!active) return;
          const realWaveform = b64 ? await extractWaveformData(b64) : null;
          newClipsToInsert.push({
            id: `clip-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            char: ch,
            charCode: ch.charCodeAt(0),
            startTime: currentNewTime,
            duration: dur,
            originalDuration: dur,
            trackId: '',
            b64,
            waveform: realWaveform || generateWaveformData(dur, ch.charCodeAt(0)),
            envelope: [...DEFAULT_ENVELOPE],
          });
          currentNewTime += dur;
        }

        const replacedClips = allClips.slice(insertIdx, insertIdx + replaceCount);
        const replacedIds = new Set(replacedClips.map(c => c.id));

        let endOfPrecedingSegment = 0;
        let rawPrecedingEndIdx = 0;
        if (newClipsToInsert.length > 0) {
          const lastInserted = newClipsToInsert[newClipsToInsert.length - 1];
          endOfPrecedingSegment = lastInserted.startTime + lastInserted.duration;
          rawPrecedingEndIdx = getRawIndex(currentText, newChars.length - suffixLen - 1) + 1;
        } else {
          if (prefixLen > 0 && allClips[prefixLen - 1]) {
            const prevClip = allClips[prefixLen - 1];
            endOfPrecedingSegment = prevClip.startTime + prevClip.duration;
            rawPrecedingEndIdx = getRawIndex(currentText, prefixLen - 1) + 1;
          } else {
            endOfPrecedingSegment = 0;
            rawPrecedingEndIdx = 0;
          }
        }

        const rawSuffixStartIdx = suffixLen > 0 ? getRawIndex(currentText, newChars.length - suffixLen) : currentText.length;
        const pauseStringBetween = currentText.substring(rawPrecedingEndIdx, rawSuffixStartIdx);
        const pauseStringBetweenUnits = countPauseUnits(pauseStringBetween);
        const requiredSuffixStartTime = endOfPrecedingSegment + pauseStringBetweenUnits * SPACE_UNIT_SECONDS;

        let netShift = 0;
        if (allClips[prefixLen + replaceCount]) {
          netShift = requiredSuffixStartTime - allClips[prefixLen + replaceCount].startTime;
        }

        const hasPreceding = insertIdx > 0 && allClips[insertIdx - 1];
        const baseTrackIdx = hasPreceding
          ? currentTracks.findIndex(t => t.id === allClips[insertIdx - 1].trackId)
          : 0;
        const validBaseTrackIdx = baseTrackIdx === -1 ? 0 : baseTrackIdx;

        newClipsToInsert.forEach((clip, idx) => {
          const trackIdx = hasPreceding
            ? (validBaseTrackIdx + 1 + idx) % currentTracks.length
            : idx % currentTracks.length;
          clip.trackId = currentTracks[trackIdx].id;
        });

        const shiftedIds = new Set(allClips.slice(insertIdx + replaceCount).map(c => c.id));

        setTracks(prevTracks => {
          const updated = prevTracks.map(t => {
            const keptClips = t.clips.filter(c => !replacedIds.has(c.id));

            const shiftedClips = keptClips.map(c => {
              if (shiftedIds.has(c.id)) {
                return {
                  ...c,
                  startTime: Math.max(0, c.startTime + netShift)
                };
              }
              return c;
            });

            const newClipsForTrack = newClipsToInsert.filter(c => c.trackId === t.id);

            return {
              ...t,
              clips: [...shiftedClips, ...newClipsForTrack].sort((a, b) => a.startTime - b.startTime)
            };
          });

          queueTextSync(updated);
          return updated;
        });

        if (newClipsToInsert.length > 0) {
          setStatus(`Inserted ${newClipsToInsert.length} clip${newClipsToInsert.length !== 1 ? 's' : ''}`);
        } else if (replaceCount > 0) {
          setStatus(`Removed ${replaceCount} clip${replaceCount !== 1 ? 's' : ''}`);
        }
        return;
      }

      const newChars = currentText.slice(prev.length);
      if (!newChars) return;

      let trailingPauseUnits = 0;
      for (let i = prev.length - 1; i >= 0; i--) {
        const units = getPauseUnits(prev[i]);
        if (units === 0) break;
        trailingPauseUnits += units;
      }
      const trailingPauseGap = trailingPauseUnits * SPACE_UNIT_SECONDS;

      const newClipsData = [];
      for (const ch of [...newChars]) {
        if (!active) return;
        if (isAudioPause(ch)) {
          newClipsData.push({ isPause: true, units: getPauseUnits(ch) });
          continue;
        }
        const b64 = getCharAudioB64(ch, selectedVoice);
        const dur = await getMp3Duration(b64);
        if (!active) return;
        const realWaveform = b64 ? await extractWaveformData(b64) : null;
        newClipsData.push({ ch, b64, dur, charCode: ch.charCodeAt(0), waveform: realWaveform || generateWaveformData(dur, ch.charCodeAt(0)) });
      }

      if (!active) return;

      setTracks(prevTracks => {
        if (!active) return prevTracks;
        let nextTime = 0;
        prevTracks.forEach(t => t.clips.forEach(c => {
          const end = c.startTime + c.duration;
          if (end > nextTime) nextTime = end;
        }));
        nextTime += trailingPauseGap;
        const totalExisting = prevTracks.reduce((s, t) => s + t.clips.length, 0);
        const updated = prevTracks.map(t => ({ ...t, clips: [...t.clips] }));
        let clipIdx = totalExisting;
        
        newClipsData.forEach(data => {
          if (data.isPause) { nextTime += data.units * SPACE_UNIT_SECONDS; return; }
          const trackIdx = clipIdx % prevTracks.length;
          updated[trackIdx].clips.push({
            id: `clip-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            char: data.ch, charCode: data.charCode,
            startTime: nextTime, duration: data.dur,
            originalDuration: data.dur,
            trackId: updated[trackIdx].id, b64: data.b64,
            waveform: data.waveform,
            envelope: [...DEFAULT_ENVELOPE],
          });
          updated[trackIdx].clips.sort((a, b) => a.startTime - b.startTime);
          nextTime += data.dur;
          clipIdx++;
        });
        return updated;
      });
      const newCount = [...newChars].filter(ch => !isAudioPause(ch)).length;
      if (newCount > 0) setStatus(`Added ${newCount} clip${newCount > 1 ? 's' : ''}`);
    }, 300);
    return () => { 
      active = false;
      if (genTimeoutRef.current) clearTimeout(genTimeoutRef.current); 
    };
  }, [text, selectedVoice, generateClipsFromText, queueTextSync, clearUndoRedo]);

  const pushUndo = useCallback((snapshot = tracks) => {
    const snapshotTracks = Array.isArray(snapshot) ? snapshot : snapshot.tracks;
    const snapshotCrossfadeMode = Array.isArray(snapshot) ? crossfadeMode : (snapshot.crossfadeMode ?? crossfadeMode);
    undoStackRef.current.push(JSON.stringify({ tracks: snapshotTracks, crossfadeMode: snapshotCrossfadeMode }));
    if (undoStackRef.current.length > 50) undoStackRef.current.shift();
    redoStackRef.current = [];
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(false);
  }, [tracks, crossfadeMode]);

  const stepHistory = useCallback((fromStack, toStack, label) => {
    if (fromStack.current.length === 0) return;
    const state = JSON.parse(fromStack.current.pop());
    const targetTracks = Array.isArray(state) ? state : state.tracks;
    const targetCrossfadeMode = Array.isArray(state) ? crossfadeMode : (state.crossfadeMode ?? crossfadeMode);
    const restoredText = textFromTracks(targetTracks);
    
    toStack.current.push(JSON.stringify({ tracks, crossfadeMode }));
    setTracks(targetTracks);
    setCrossfadeMode(targetCrossfadeMode);
    clipStartCorrectionRef.current.clear();
    isInternalSyncRef.current = true;
    if (onTextChange) onTextChange(restoredText);
    prevTextRef.current = restoredText;
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(redoStackRef.current.length > 0);
    setStatus(label);
  }, [tracks, crossfadeMode, textFromTracks, onTextChange]);

  const handleUndo = useCallback(() => stepHistory(undoStackRef, redoStackRef, 'Undo'), [stepHistory]);
  const handleRedo = useCallback(() => stepHistory(redoStackRef, undoStackRef, 'Redo'), [stepHistory]);

  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); handleUndo(); }
        if ((e.key === 'z' && e.shiftKey) || e.key === 'y') { e.preventDefault(); handleRedo(); }
      }
      if (e.code === 'Space' && !e.target.matches('input, textarea, select')) {
        e.preventDefault();
        handlePlay();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleUndo, handleRedo]);

  const stopAll = useCallback(() => {
    Object.values(sourceNodesRef.current).forEach(n => {
      try { n.stop(); } catch(e) {}
      try { n.disconnect(); } catch(e) {}
    });
    sourceNodesRef.current = {};
    if (playheadAnimRef.current) { cancelAnimationFrame(playheadAnimRef.current); playheadAnimRef.current = null; }
    setIsPlaying(false);
  }, []);

  const getTotalDuration = useCallback(() => {
    let max = 0;
    tracks.forEach(t => t.clips.forEach(c => { if (c.startTime + c.duration > max) max = c.startTime + c.duration; }));
    return Math.max(max, 5);
  }, [tracks]);

  const handleExportMix = useCallback(async () => {
    if (totalClips === 0) return;
    setIsExporting(true);
    setStatus('Preparing export timeline...');

    try {
      const ctx = getAudioContext();
      if (!ctx) throw new Error("Audio context is unavailable");

      const totalDur = getTotalDuration();
      const sampleRate = 44100;
      const offlineCtx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(
        2, 
        Math.ceil(sampleRate * totalDur), 
        sampleRate
      );

      const decodedBuffersMap = new Map();
      const activeClips = tracks.flatMap(t => t.clips);

      setStatus('Decoding syllables offline...');
      await Promise.all(activeClips.map(async (clip) => {
        if (clip.b64) {
          try {
            const arrayBuf = b64ToArrayBuffer(clip.b64);
            const decoded = await ctx.decodeAudioData(arrayBuf.slice(0));
            decodedBuffersMap.set(clip.id, decoded);
          } catch (e) {
            console.warn(`Failed to decode clip ${clip.id}, falling back to tone.`, e);
          }
        }
      }));

      const masterGain = offlineCtx.createGain();
      masterGain.gain.setValueAtTime(1.0, 0);

      const limiter = offlineCtx.createDynamicsCompressor();
      limiter.threshold.value = -1;
      limiter.knee.value = 0;
      limiter.ratio.value = 20;
      limiter.attack.value = 0.001;
      limiter.release.value = 0.05;
      masterGain.connect(limiter);
      limiter.connect(offlineCtx.destination);

      const anySoloed = tracks.some(t => t.soloed);

      tracks.forEach(track => {
        const effVol = getEffectiveTrackVolume(track, anySoloed, masterVolume);
        if (effVol <= 0) return;

        const trackGain = offlineCtx.createGain();
        trackGain.gain.setValueAtTime(effVol, 0);
        trackGain.connect(masterGain);

        track.clips.forEach(clip => {
          const clipStart = clip.startTime;
          const clipDuration = clip.duration;
          const playRate = (clip.originalDuration && clip.duration) ? clip.originalDuration / clip.duration : 1.0;

          const clipGainNode = offlineCtx.createGain();
          clipGainNode.connect(trackGain);

          const env = crossfadeMode === 'manual' ? (clip.envelope || DEFAULT_ENVELOPE) : DEFAULT_ENVELOPE;
          
          let evalTimes = new Set();
          const STEPS = Math.max(50, Math.ceil(clipDuration * 100));
          for (let i = 0; i <= STEPS; i++) evalTimes.add(i / STEPS);
          
          activeClips.forEach(other => {
            if (other.id === clip.id) return;
            const overlapStart = Math.max(clip.startTime, other.startTime);
            const overlapEnd = Math.min(clip.startTime + clip.duration, other.startTime + other.duration);
            if (overlapEnd > overlapStart) {
              if (overlapStart >= clip.startTime && overlapStart <= clip.startTime + clip.duration) {
                evalTimes.add((overlapStart - clip.startTime) / clip.duration);
              }
              if (overlapEnd >= clip.startTime && overlapEnd <= clip.startTime + clip.duration) {
                evalTimes.add((overlapEnd - clip.startTime) / clip.duration);
              }
            }
          });
          
          env.forEach(wp => evalTimes.add(wp.time));
          const times = Array.from(evalTimes).sort((a, b) => a - b);

          times.forEach((tNorm, index) => {
            const globalT = clip.startTime + tNorm * clip.duration;
            const finalGain = getEffectiveGainAtTime(clip, globalT, activeClips, env, crossfadeMode);
            const timeAtCtx = clipStart + tNorm * clip.duration;

            if (index === 0) clipGainNode.gain.setValueAtTime(finalGain, timeAtCtx);
            else clipGainNode.gain.linearRampToValueAtTime(finalGain, timeAtCtx);
          });

          const src = offlineCtx.createBufferSource();
          let audioBuffer = decodedBuffersMap.get(clip.id);
          if (!audioBuffer) {
            audioBuffer = createOfflineFallbackTone(offlineCtx, clip, clipDuration, playRate);
          }

          src.buffer = audioBuffer;
          src.playbackRate.value = playRate;
          src.connect(clipGainNode);
          src.start(clipStart);
          src.stop(clipStart + audioBuffer.duration / playRate);
        });
      });

      setStatus('Rendering mix...');
      const renderedBuffer = await offlineCtx.startRendering();

      setStatus('Encoding to WAV...');
      const wavBlob = audioBufferToWav(renderedBuffer);
      const url = URL.createObjectURL(wavBlob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `geez-fidel-mix-${Date.now()}.wav`;
      a.click();
      
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      setStatus('Mix exported successfully!');
    } catch (err) {
      console.error(err);
      setStatus(`Export failed: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  }, [tracks, masterVolume, crossfadeMode, totalClips, getTotalDuration]);

  // handlePlay now accepts an optional 'startPosOverride' parameter.
  // This allows the caller (like ruler click) to instantly force the playhead
  // to a specific timestamp, stopping existing playback and starting from that offset.
  // If no override is provided, it falls back to the current stateful playheadPos.
  const handlePlay = useCallback(async (startPosOverride) => {
    // If we are currently playing and NO offset override was specified,
    // this acts as a true PAUSE. We stop context nodes, leave the playhead
    // precisely where it was, and exit.
    if (isPlaying && startPosOverride === undefined) { 
      stopAll(); 
      return; 
    }

    // If an offset override was provided while playing, we first halt the active loops
    // cleanly before initiating the new playback scheduling at the scrubbed location.
    if (isPlaying && startPosOverride !== undefined) {
      stopAll();
    }

    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') await ctx.resume();
    const now = ctx.currentTime;
    playStartTimeRef.current = now;

    // Establish the timeline starting position (offset) in seconds.
    let startOffset = startPosOverride !== undefined ? startPosOverride : playheadPos;

    // Find the total duration bounds of all currently laid out tracks
    let maxEnd = 0;
    tracks.forEach(track => {
      track.clips.forEach(clip => {
        const end = clip.startTime + clip.duration;
        if (end > maxEnd) maxEnd = end;
      });
    });

    // If the playhead is already sitting near the absolute end of the timeline (or past it),
    // we wrap it back around to 0 to restart playback from the beginning.
    if (startOffset >= maxEnd - 0.05) {
      startOffset = 0;
    }

    // Persist and visually commit the active starting playhead location.
    setPlayheadPos(startOffset);
    playStartTimeOffsetRef.current = startOffset;

    const anySoloed = tracks.some(t => t.soloed);
    tracks.forEach(track => {
      if (trackGainRefsRef.current[track.id]) trackGainRefsRef.current[track.id].disconnect();
      const gain = ctx.createGain();
      gain.connect(masterGainRef.current);
      trackGainRefsRef.current[track.id] = gain;
    });

    const activeClips = tracks.flatMap(t => t.clips);

    tracks.forEach(track => {
      const targetGain = trackGainRefsRef.current[track.id];
      if (!targetGain) return;
      targetGain.gain.setValueAtTime(getEffectiveTrackVolume(track, anySoloed, masterVolume), now);

      track.clips.forEach(clip => {
        const clipEnd = clip.startTime + clip.duration;
        if (clipEnd <= startOffset) {
          // Clip occurred completely before the resume playhead; skip scheduling it.
          return;
        }

        const playRate = (clip.originalDuration && clip.duration) ? clip.originalDuration / clip.duration : 1.0;
        
        let scheduleTime = now;
        let bufferStartOffset = 0;
        let durationToPlay = clip.duration;

        if (clip.startTime >= startOffset) {
          // Clip starts in the future relative to playhead start time
          scheduleTime = now + (clip.startTime - startOffset);
          bufferStartOffset = 0;
          durationToPlay = clip.duration;
        } else {
          // Playhead starts directly inside the middle of this clip segment.
          // Schedule playback immediately, and determine how deep into the buffer we should jump.
          scheduleTime = now;
          const elapsedInClip = startOffset - clip.startTime; // timeline elapsed seconds
          bufferStartOffset = elapsedInClip * playRate;        // convert timeline seconds to raw buffer seconds
          durationToPlay = clip.duration - elapsedInClip;      // remaining timeline duration to play
        }

        const env = crossfadeMode === 'manual' ? (clip.envelope || DEFAULT_ENVELOPE) : DEFAULT_ENVELOPE;

        const clipGainNode = ctx.createGain();
        clipGainNode.connect(targetGain);

        let evalTimes = new Set();
        const STEPS = Math.max(50, Math.ceil(clip.duration * 100));
        for (let i = 0; i <= STEPS; i++) evalTimes.add(i / STEPS);
        
        activeClips.forEach(other => {
          if (other.id === clip.id) return;
          const overlapStart = Math.max(clip.startTime, other.startTime);
          const overlapEnd = Math.min(clip.startTime + clip.duration, other.startTime + other.duration);
          if (overlapEnd > overlapStart) {
            if (overlapStart >= clip.startTime && overlapStart <= clip.startTime + clip.duration) {
              evalTimes.add((overlapStart - clip.startTime) / clip.duration);
            }
            if (overlapEnd >= clip.startTime && overlapEnd <= clip.startTime + clip.duration) {
              evalTimes.add((overlapEnd - clip.startTime) / clip.duration);
            }
          }
        });
        
        env.forEach(wp => evalTimes.add(wp.time));
        const times = Array.from(evalTimes).sort((a, b) => a - b);

        // First establish the current automated volume matching exactly where the playhead
        // started within the clip, and set it immediately at context time `now`.
        const initialGain = getEffectiveGainAtTime(clip, Math.max(clip.startTime, startOffset), activeClips, env, crossfadeMode);
        clipGainNode.gain.setValueAtTime(initialGain, now);

        times.forEach((tNorm) => {
          const globalT = clip.startTime + tNorm * clip.duration;
          // Only schedule ramp envelopes that occur in the future relative to our playhead.
          if (globalT > startOffset) {
            const finalGain = getEffectiveGainAtTime(clip, globalT, activeClips, env, crossfadeMode);
            const timeAtCtx = now + (globalT - startOffset);
            clipGainNode.gain.linearRampToValueAtTime(finalGain, timeAtCtx);
          }
        });

        if (!clip.b64) {
          sourceNodesRef.current[clip.id] = playFallbackTone(ctx, clip, durationToPlay, playRate, clipGainNode, scheduleTime);
          return;
        }

        try {
          const arrayBuf = b64ToArrayBuffer(clip.b64);
          ctx.decodeAudioData(arrayBuf.slice(0)).then(audioBuffer => {
            const src = ctx.createBufferSource();
            src.buffer = audioBuffer;
            src.playbackRate.value = playRate;
            src.connect(clipGainNode);
            
            // Play from the calculated buffer index, running only for the remaining portion of the clip duration.
            src.start(scheduleTime, bufferStartOffset);
            src.stop(scheduleTime + durationToPlay);
            
            sourceNodesRef.current[clip.id] = src;
          }).catch(() => {
            sourceNodesRef.current[clip.id] = playFallbackTone(ctx, clip, durationToPlay, playRate, clipGainNode, scheduleTime);
          });
        } catch(e) { console.warn('Clip decode:', e); }
      });
    });

    setIsPlaying(true);
    setStatus('Playing mix...');

    // The animation updates visual progress based on elapsed time since playback resumed,
    // added to the playhead offset at start.
    const animate = () => {
      const elapsed = (getAudioContext()?.currentTime || 0) - playStartTimeRef.current;
      const currentPos = playStartTimeOffsetRef.current + elapsed;
      setPlayheadPos(currentPos);
      
      const scrollEl = workspaceScrollRef.current;
      if (scrollEl) {
        const px = currentPos * zoom;
        const sl = scrollEl.scrollLeft;
        const vw = scrollEl.clientWidth - CONTROL_PANEL_WIDTH;
        if (px > sl + vw - 50 || px < sl) scrollEl.scrollLeft = px - 100;
      }
      
      if (currentPos < maxEnd) { 
        playheadAnimRef.current = requestAnimationFrame(animate); 
      } else { 
        // Once the playhead hits the absolute end of the DAW track, we halt sounds
        // and reset the playhead directly to 0 (start).
        stopAll(); 
        setPlayheadPos(0); 
        setStatus('Playback complete'); 
      }
    };
    playheadAnimRef.current = requestAnimationFrame(animate);
  }, [isPlaying, tracks, masterVolume, stopAll, zoom, crossfadeMode, playheadPos]);

  // stopAll stops the audio nodes and sets isPlaying to false (retaining playheadPos).
  // handleStop triggers this lower-level sound kill and explicitly resets the playhead position
  // to 0 so that clicking play afterwards initiates playback from the absolute beginning.
  const handleStop = useCallback(() => {
    stopAll();
    setPlayheadPos(0);
    setStatus('Playback stopped');
  }, [stopAll]);

  const handleAddTrack = useCallback(() => {
    pushUndo(tracks);
    setTracks(prev => {
      const nextIndex = prev.length + 1;
      return [
        ...prev,
        {
          id: `track-${nextIndex}`,
          label: `Track ${nextIndex}`,
          clips: [],
          volume: 0.7,
          muted: false,
          soloed: false,
        }
      ];
    });
    setStatus('Added new track');
  }, [tracks, pushUndo]);

  const dragRef = useRef({ active: false, cursorOffsetX: 0, cursorOffsetY: 0 });

  // Drag & drop support with Pointer Capture
  const handleClipPointerDown = useCallback((e, clip, trackId) => {
    if (e.target.classList.contains('clip-resize-left') || e.target.classList.contains('clip-resize-right') ||
        e.target.classList.contains('clip-delete-btn') || e.target.closest('.envelope-area') ||
        e.target.classList.contains('effect-btn')) return;
    
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();

    const workspace = workspaceRef.current;
    if (!workspace) return;
    const workspaceRect = workspace.getBoundingClientRect();

    let activeSelectionIds = new Set(selectedClipIds);
    const isModifier = e.shiftKey || e.ctrlKey || e.metaKey;

    if (!activeSelectionIds.has(clip.id)) {
      if (!isModifier) {
        activeSelectionIds = new Set([clip.id]);
      } else {
        activeSelectionIds.add(clip.id);
      }
      setSelectedClipIds(activeSelectionIds);
    }

    setDraggingClipIds(activeSelectionIds);

    const clickedEl = e.currentTarget.getBoundingClientRect();
    const drag = dragRef.current;
    drag.active = true;
    drag.cursorOffsetX = e.clientX - clickedEl.left;
    drag.cursorOffsetY = e.clientY - clickedEl.top;

    pushUndo(tracks);

    const dragStartTracks = tracks;

    const dragStartStates = [];
    dragStartTracks.forEach((t, tIdx) => {
      t.clips.forEach(c => {
        if (activeSelectionIds.has(c.id)) {
          const el = trackDomRefs.current[t.id]?.querySelector(`[data-clip-id="${c.id}"]`);
          dragStartStates.push({
            clip: c,
            origTrackId: t.id,
            origTrackIndex: tIdx,
            origStartTime: c.startTime,
            origEl: el
          });
        }
      });
    });

    const primaryStart = dragStartStates.find(s => s.clip.id === clip.id);
    if (!primaryStart) return;

    const ghosts = [];
    dragStartStates.forEach(state => {
      if (!state.origEl) return;
      state.origEl.classList.add('drag-origin');
      const ghost = state.origEl.cloneNode(true);
      ghost.classList.remove('drag-origin');
      ghost.classList.add('dragging');
      ghost.style.position = 'absolute';
      
      const elRect = state.origEl.getBoundingClientRect();
      ghost.style.top = `${elRect.top - workspaceRect.top}px`;
      ghost.style.left = `${elRect.left - workspaceRect.left}px`;
      ghost.style.width = `${elRect.width}px`;
      ghost.style.height = `${elRect.height}px`;
      ghost.style.zIndex = '1000';
      ghost.style.pointerEvents = 'none';
      
      workspace.appendChild(ghost);
      ghosts.push({ ghostEl: ghost, state });
    });

    const onMove = (ev) => {
      if (!drag.active) return;

      const primaryLane = trackDomRefs.current[primaryStart.origTrackId]?.querySelector('.track-lane');
      if (!primaryLane) return;

      let gLeft = ev.clientX - workspaceRect.left - drag.cursorOffsetX;
      let dll = (gLeft + workspaceRect.left) - primaryLane.getBoundingClientRect().left;
      if (dll < 0) dll = 0;
      let fd = dll / zoom;
      if (snapEnabled) fd = Math.round(fd * 10) / 10;

      let targetTrackId = primaryStart.origTrackId;
      dragStartTracks.forEach(t => {
        const row = trackDomRefs.current[t.id];
        if (row) {
          const r = row.getBoundingClientRect();
          if (ev.clientY >= r.top && ev.clientY <= r.bottom) targetTrackId = t.id;
        }
      });

      const targetTrackIdx = dragStartTracks.findIndex(t => t.id === targetTrackId);
      const trackDelta = targetTrackIdx - primaryStart.origTrackIndex;
      const primaryTimeDelta = fd - primaryStart.origStartTime;

      const finalStates = dragStartStates.map(state => {
        const tIndex = Math.max(0, Math.min(dragStartTracks.length - 1, state.origTrackIndex + trackDelta));
        const tId = dragStartTracks[tIndex].id;
        
        let clipTime = state.origStartTime + primaryTimeDelta;
        if (clipTime < 0) clipTime = 0;
        
        return {
          clipId: state.clip.id,
          targetTrackId: tId,
          targetStartTime: clipTime,
        };
      });

      const updated = dragStartTracks.map(t => {
        const staticClips = t.clips.filter(c => !activeSelectionIds.has(c.id));
        
        const targetDraggedClips = finalStates
          .filter(fs => fs.targetTrackId === t.id)
          .map(fs => {
            const originalClip = dragStartStates.find(s => s.clip.id === fs.clipId).clip;
            return {
              ...originalClip,
              startTime: fs.targetStartTime,
            };
          });

        const resolvedClips = resolveTrackLayout(staticClips, targetDraggedClips);

        return {
          ...t,
          clips: resolvedClips.sort((a, b) => a.startTime - b.startTime)
        };
      });

      setTracks(updated);

      ghosts.forEach(({ ghostEl, state }) => {
        const tIndex = Math.max(0, Math.min(dragStartTracks.length - 1, state.origTrackIndex + trackDelta));
        const tId = dragStartTracks[tIndex].id;
        
        const resolvedTrack = updated.find(track => track.id === tId);
        const resolvedClip = resolvedTrack?.clips.find(c => c.id === state.clip.id);
        
        let clipTime = resolvedClip ? resolvedClip.startTime : (state.origStartTime + primaryTimeDelta);
        if (clipTime < 0) clipTime = 0;

        const lane = trackDomRefs.current[tId]?.querySelector('.track-lane');
        const row = trackDomRefs.current[tId];
        if (lane && row) {
          const laneRect = lane.getBoundingClientRect();
          const rowRect = row.getBoundingClientRect();
          ghostEl.style.left = `${laneRect.left - workspaceRect.left + clipTime * zoom}px`;
          ghostEl.style.top = `${rowRect.top - workspaceRect.top + (state.origEl.getBoundingClientRect().top - trackDomRefs.current[state.origTrackId].getBoundingClientRect().top)}px`;
          
          const badge = ghostEl.querySelector('.clip-shift-badge');
          if (badge) badge.innerText = `${clipTime.toFixed(2)}s`;
        }
      });
    };

    const onUp = (ev) => {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (err) {}

      if (!drag.active) return;
      drag.active = false;
      setDraggingClipIds(null);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('keydown', onKeyDown);

      ghosts.forEach(({ ghostEl }) => {
        if (ghostEl?.parentNode) ghostEl.parentNode.removeChild(ghostEl);
      });
      dragStartStates.forEach(state => {
        if (state.origEl) state.origEl.classList.remove('drag-origin');
      });

      const primaryLane = trackDomRefs.current[primaryStart.origTrackId]?.querySelector('.track-lane');
      if (!primaryLane) return;

      let gLeft = ev.clientX - workspaceRect.left - drag.cursorOffsetX;
      let dll = (gLeft + workspaceRect.left) - primaryLane.getBoundingClientRect().left;
      if (dll < 0) dll = 0;
      let fd = dll / zoom;
      if (snapEnabled) fd = Math.round(fd * 10) / 10;

      let targetTrackId = primaryStart.origTrackId;
      dragStartTracks.forEach(t => {
        const row = trackDomRefs.current[t.id];
        if (row) {
          const r = row.getBoundingClientRect();
          if (ev.clientY >= r.top && ev.clientY <= r.bottom) targetTrackId = t.id;
        }
      });

      const targetTrackIdx = dragStartTracks.findIndex(t => t.id === targetTrackId);
      const trackDelta = targetTrackIdx - primaryStart.origTrackIndex;
      const primaryTimeDelta = fd - primaryStart.origStartTime;

      clipStartCorrectionRef.current.clear();

      const finalStates = dragStartStates.map(state => {
        const tIndex = Math.max(0, Math.min(dragStartTracks.length - 1, state.origTrackIndex + trackDelta));
        const tId = dragStartTracks[tIndex].id;
        
        let clipTime = state.origStartTime + primaryTimeDelta;
        if (clipTime < 0) clipTime = 0;
        
        return {
          clipId: state.clip.id,
          targetTrackId: tId,
          targetStartTime: clipTime,
        };
      });

      const finalTracks = dragStartTracks.map(t => {
        const staticClips = t.clips.filter(c => !activeSelectionIds.has(c.id));
        
        const targetDraggedClips = finalStates
          .filter(fs => fs.targetTrackId === t.id)
          .map(fs => {
            const originalClip = dragStartStates.find(s => s.clip.id === fs.clipId).clip;
            return {
              ...originalClip,
              startTime: fs.targetStartTime,
            };
          });

        const resolvedClips = resolveTrackLayout(staticClips, targetDraggedClips);

        return {
          ...t,
          clips: resolvedClips.sort((a, b) => a.startTime - b.startTime)
        };
      });

      setTracks(finalTracks);
      queueTextSync(finalTracks);
      setStatus(`Group of ${activeSelectionIds.size} clips shifted`);
    };

    const onKeyDown = (keyEv) => {
      if (keyEv.key === 'Escape') {
        drag.active = false;
        setDraggingClipIds(null);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        window.removeEventListener('keydown', onKeyDown);

        setTracks(dragStartTracks);

        ghosts.forEach(({ ghostEl }) => {
          if (ghostEl?.parentNode) ghostEl.parentNode.removeChild(ghostEl);
        });
        dragStartStates.forEach(state => {
          if (state.origEl) state.origEl.classList.remove('drag-origin');
        });
        setStatus('Drag cancelled');
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('keydown', onKeyDown);
  }, [tracks, zoom, snapEnabled, selectedClipIds, pushUndo, queueTextSync]);

  // Multitouch Highlight / Marquee Selector supporting touch devices
  const handleWorkspacePointerDown = useCallback((e) => {
    // We explicitly bypass elements belonging to '.ruler-row' (including the ruler-spacer and timeline-ruler).
    // If we do not return early here, the selection marquee runs e.preventDefault() which blocks
    // normal mouseup sequence propagation, completely breaking the ruler onClick handlers.
    if (e.target.closest('.track-control-panel') || e.target.closest('.audio-toolbar') ||
        e.target.closest('.audio-clip') || e.target.closest('.zoom-controls') ||
        e.target.closest('.voice-selector-mini') || e.target.closest('.close-track-btn') ||
        e.target.closest('.mobile-scroll-hint') || e.target.closest('.ruler-row')) {
      return;
    }

    e.preventDefault();

    e.currentTarget.setPointerCapture(e.pointerId);

    const workspace = workspaceRef.current;
    if (!workspace) return;
    const workspaceRect = workspace.getBoundingClientRect();
    const startX = e.clientX - workspaceRect.left;
    const startY = e.clientY - workspaceRect.top;

    const isModifier = e.shiftKey || e.ctrlKey || e.metaKey;

    const marquee = document.createElement('div');
    marquee.className = 'marquee-box';
    marquee.style.position = 'absolute';
    marquee.style.left = `${startX}px`;
    marquee.style.top = `${startY}px`;
    marquee.style.width = '0px';
    marquee.style.height = '0px';
    marquee.style.border = '1px dashed var(--clip-accent-waveform)';
    marquee.style.backgroundColor = 'var(--clip-accent-bg)';
    marquee.style.zIndex = '1000';
    marquee.style.pointerEvents = 'none';
    workspace.appendChild(marquee);

    const onPointerMove = (moveEv) => {
      const currentX = moveEv.clientX - workspaceRect.left;
      const currentY = moveEv.clientY - workspaceRect.top;
      
      const x = Math.min(startX, currentX);
      const y = Math.min(startY, currentY);
      const w = Math.abs(startX - currentX);
      const h = Math.abs(startY - currentY);
      
      marquee.style.left = `${x}px`;
      marquee.style.top = `${y}px`;
      marquee.style.width = `${w}px`;
      marquee.style.height = `${h}px`;
    };

    const onPointerUp = (upEv) => {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (err) {}

      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);

      const marqueeRect = marquee.getBoundingClientRect();
      if (marquee.parentNode) marquee.parentNode.removeChild(marquee);

      if (marqueeRect.width < 5 && marqueeRect.height < 5) {
        if (!isModifier) setSelectedClipIds(new Set());
        return;
      }

      const newlySelected = new Set(isModifier ? selectedClipIds : []);

      tracks.forEach(track => {
        track.clips.forEach(clip => {
          const clipEl = trackDomRefs.current[track.id]?.querySelector(`[data-clip-id="${clip.id}"]`);
          if (clipEl) {
            const clipRect = clipEl.getBoundingClientRect();
            const intersects = !(
              marqueeRect.right < clipRect.left ||
              marqueeRect.left > clipRect.right ||
              marqueeRect.bottom < clipRect.top ||
              marqueeRect.top > clipRect.bottom
            );
            if (intersects) newlySelected.add(clip.id);
          }
        });
      });

      setSelectedClipIds(newlySelected);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
  }, [tracks, selectedClipIds]);

  const handleTrimPointerDown = useCallback((e, clip, trackId, edge) => {
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    pushUndo(tracks);
    const startX = e.clientX;
    const origStart = clip.startTime; const origDur = clip.duration;
    const onMove = (ev) => {
      const dx = (ev.clientX - startX) / zoom;
      setTracks(prev => prev.map(t => {
        if (t.id !== trackId) return t;
        return { ...t, clips: t.clips.map(c => {
          if (c.id !== clip.id) return c;
          if (edge === 'left') {
            let ns = origStart + dx, nd = origDur - dx;
            if (ns < 0) { nd += ns; ns = 0; }
            if (nd * zoom < MIN_CLIP_WIDTH) { ns = origStart + origDur - MIN_CLIP_WIDTH / zoom; nd = MIN_CLIP_WIDTH / zoom; }
            return { ...c, startTime: ns, duration: nd };
          } else {
            let nd = origDur + dx;
            if (nd * zoom < MIN_CLIP_WIDTH) nd = MIN_CLIP_WIDTH / zoom;
            return { ...c, duration: nd };
          }
        }).sort((a, b) => a.startTime - b.startTime) };
      }));
    };
    const onUp = () => {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (err) {}
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      clipStartCorrectionRef.current.clear();
      setTracks(prev => { queueTextSync(prev); return prev; });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [tracks, zoom, pushUndo, queueTextSync]);

  const wpTapTimerRef = useRef(null);
  const wpTapTargetRef = useRef(null);

  const handleWaypointPointerDown = useCallback((e, clip, trackId, wpIndex) => {
    e.preventDefault(); e.stopPropagation();

    const tapKey = `${clip.id}:${wpIndex}`;
    const now = Date.now();
    if (wpTapTargetRef.current === tapKey && wpTapTimerRef.current && (now - wpTapTimerRef.current) < 350) {
      wpTapTimerRef.current = null;
      wpTapTargetRef.current = null;
      if (wpIndex !== 0 && wpIndex !== (clip.envelope?.length || 0) - 1) {
        pushUndo(tracks);
        setTracks(prev => prev.map(t => {
          if (t.id !== trackId) return t;
          return { ...t, clips: t.clips.map(c => {
            if (c.id !== clip.id) return c;
            const newEnv = (c.envelope || []).filter((_, i) => i !== wpIndex);
            return { ...c, envelope: newEnv.length >= 2 ? newEnv : [...DEFAULT_ENVELOPE] };
          }) };
        }));
        setStatus(`Removed waypoint`);
      }
      return;
    }
    wpTapTargetRef.current = tapKey;
    wpTapTimerRef.current = now;

    pushUndo(tracks);
    const clipEl = trackDomRefs.current[trackId]?.querySelector(`[data-clip-id="${clip.id}"]`);
    if (!clipEl) return;

    const onMove = (ev) => {
      wpTapTargetRef.current = null;
      const clipRect = clipEl.getBoundingClientRect();
      const relX = (ev.clientX - clipRect.left) / clipRect.width;
      const relY = 1 - ((ev.clientY - clipRect.top) / clipRect.height);
      const newTime = (wpIndex === 0 || wpIndex === clip.envelope.length - 1)
        ? clip.envelope[wpIndex].time
        : Math.max(0.01, Math.min(0.99, relX));
      const newGain = Math.max(0, Math.min(1, relY));

      setTracks(prev => prev.map(t => {
        if (t.id !== trackId) return t;
        return { ...t, clips: t.clips.map(c => {
          if (c.id !== clip.id) return c;
          const newEnv = [...c.envelope];
          newEnv[wpIndex] = { time: newTime, gain: newGain };
          newEnv.sort((a, b) => a.time - b.time);
          return { ...c, envelope: newEnv };
        }) };
      }));
    };
    const onUp = (ev) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      if (Math.abs(ev.clientX - e.clientX) > 5 || Math.abs(ev.clientY - e.clientY) > 5) wpTapTargetRef.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [tracks, pushUndo]);

  const handleEnvelopeClick = useCallback((e, clip, trackId) => {
    e.stopPropagation();
    if (crossfadeMode !== 'manual') return;
    if (e.target.classList?.contains('envelope-waypoint')) return;
    const clipEl = trackDomRefs.current[trackId]?.querySelector(`[data-clip-id="${clip.id}"]`);
    if (!clipEl) return;
    const clipRect = clipEl.getBoundingClientRect();
    const relX = Math.max(0.05, Math.min(0.95, (e.clientX - clipRect.left) / clipRect.width));
    const relY = 1 - Math.max(0, Math.min(1, (e.clientY - clipRect.top) / clipRect.height));
    pushUndo(tracks);
    setTracks(prev => prev.map(t => {
      if (t.id !== trackId) return t;
      return { ...t, clips: t.clips.map(c => {
        if (c.id !== clip.id) return c;
        const newEnv = [...(c.envelope || [...DEFAULT_ENVELOPE]), { time: relX, gain: relY }].sort((a, b) => a.time - b.time);
        return { ...c, envelope: newEnv };
      }) };
    }));
  }, [tracks, pushUndo, crossfadeMode]);

  const handleSetFade = useCallback((clipId, trackId, fadeType) => {
    pushUndo(tracks);
    const newEnv = makeEnvelope(fadeType);
    setTracks(prev => prev.map(t => {
      if (t.id !== trackId) return t;
      return { ...t, clips: t.clips.map(c => c.id === clipId ? { ...c, envelope: newEnv } : c) };
    }));
    setStatus(`Applied ${fadeType || 'flat'} envelope`);
  }, [tracks, pushUndo]);

  const handleVolumeChange = useCallback((trackId, val) => {
    setTracks(prev => prev.map(t => t.id === trackId ? { ...t, volume: parseFloat(val) } : t));
  }, []);
  
  const handleMuteToggle = useCallback((trackId) => {
    pushUndo(tracks);
    setTracks(prev => prev.map(t => t.id === trackId ? { ...t, muted: !t.muted } : t));
  }, [tracks, pushUndo]);
  
  const handleSoloToggle = useCallback((trackId) => {
    pushUndo(tracks);
    setTracks(prev => {
      const was = prev.find(t => t.id === trackId)?.soloed;
      return was ? prev.map(t => ({ ...t, soloed: false })) : prev.map(t => t.id === trackId ? { ...t, soloed: true } : { ...t, soloed: false });
    });
  }, [tracks, pushUndo]);
  
  const handleClearTrack = useCallback((trackId) => {
    pushUndo(tracks);
    setSelectedClipIds(new Set());
    setTracks(prev => {
      const updated = prev.map(t => t.id === trackId ? { ...t, clips: [] } : t);
      clipStartCorrectionRef.current.clear();
      queueTextSync(updated);
      return updated;
    });
    setStatus(`Cleared ${trackId}`);
  }, [tracks, pushUndo, queueTextSync]);
  
  const handleDeleteClip = useCallback((trackId, clipId) => {
    pushUndo(tracks);
    setSelectedClipIds(prev => {
      const copy = new Set(prev);
      copy.delete(clipId);
      return copy;
    });
    setTracks(prev => {
      const updated = prev.map(t => trackId === t.id ? { ...t, clips: t.clips.filter(c => c.id !== clipId) } : t);
      clipStartCorrectionRef.current.clear();
      queueTextSync(updated);
      return updated;
    });
  }, [tracks, pushUndo, queueTextSync]);

  // Calculates the DAW timeline offset coordinate relative to where the user clicked on the ruler or lane.
  // - If currently playing: Stops active audio immediately, then restarts playback from the scrubbed offset.
  // - If paused/stopped: Repositions the visual playhead directly to the clicked timestamp without triggering audio.
  const handleRulerClick = useCallback((e) => {
    // e.currentTarget is strictly the '.timeline-ruler' wrapper div
    const rect = e.currentTarget.getBoundingClientRect();
    const relativeX = e.clientX - rect.left;
    
    // Ensure we ignore negative click coordinates
    if (relativeX < 0) return;
    const clickedTime = relativeX / zoom;

    if (isPlaying) {
      // If audio is active, stop and instantly resume playback from the clicked timestamp
      handlePlay(clickedTime);
    } else {
      // Otherwise, simply relocate the playhead visually
      setPlayheadPos(clickedTime);
    }
  }, [zoom, isPlaying, handlePlay]);

  const handleZoomIn = useCallback(() => setZoom(p => ZOOM_LEVELS.find(z => z > p) || p), []);
  const handleZoomOut = useCallback(() => setZoom(p => [...ZOOM_LEVELS].reverse().find(z => z < p) || p), []);
  const handleZoomFit = useCallback(() => {
    const el = workspaceScrollRef.current;
    if (!el) return;
    const ideal = (el.clientWidth - CONTROL_PANEL_WIDTH - 20) / getTotalDuration();
    setZoom(ZOOM_LEVELS.reduce((p, c) => Math.abs(c - ideal) < Math.abs(p - ideal) ? c : p));
  }, [getTotalDuration]);

  const handleReset = useCallback(async () => {
    if (!text) return;
    pushUndo(tracks);
    setSelectedClipIds(new Set());
    const clips = await generateClipsFromText(text, selectedVoice);
    const updated = tracks.map(t => ({ ...t, clips: [] }));
    clips.forEach((c, i) => updated[i % tracks.length].clips.push(c));
    updated.forEach(t => t.clips.sort((a, b) => a.startTime - b.startTime));
    clipStartCorrectionRef.current.clear();
    setTracks(updated);
    prevTextRef.current = text;
    clearUndoRedo();
    setStatus('Reset clips from text');
  }, [text, selectedVoice, tracks, generateClipsFromText, pushUndo, clearUndoRedo]);

  const handleCrossfadeModeToggle = useCallback(() => {
    const nextMode = crossfadeMode === 'automatic' ? 'manual' : 'automatic';

    if (nextMode === 'manual') {
      const allClips = tracks.flatMap(t => t.clips);
      const hasEnvelopeEdits = allClips.some(clip => !isDefaultEnvelope(clip.envelope));

      if (!hasEnvelopeEdits) {
        pushUndo({ tracks, crossfadeMode });
        const convertedTracks = tracks.map(track => ({
          ...track,
          clips: track.clips.map(clip => ({
            ...clip,
            envelope: buildAutoCrossfadeEnvelope(clip, allClips),
          })),
        }));
        setTracks(convertedTracks);
        setStatus('Converted Auto Xfade to Manual Xfade waypoints');
      } else {
        setStatus('Switched to Manual Xfade; envelope edits preserved');
      }
    }
    setCrossfadeMode(nextMode);
  }, [crossfadeMode, tracks, pushUndo]);

  const renderRulerTicks = useMemo(() => {
    const total = getTotalDuration();
    let interval = 1;
    if (zoom >= 200) interval = 0.5;
    if (zoom >= 300) interval = 0.25;
    if (zoom < 50) interval = 2;
    if (zoom < 30) interval = 5;
    const ticks = [];
    for (let t = 0; t <= total + 1; t += interval) {
      const isMajor = interval < 1 ? t % 1 === 0 : true;
      ticks.push(
        <span key={t} className={`ruler-tick ${isMajor ? 'ruler-major' : 'ruler-minor'}`} style={{ left: `${t * zoom}px` }}>
          {isMajor && (zoom >= 50 || interval >= 1) ? formatTime(t) : ''}
        </span>
      );
    }
    return ticks;
  }, [zoom, getTotalDuration]);

  const renderWaveform = useCallback((waveform, color, width) => {
    if (!waveform || waveform.length === 0 || width < 4) return null;
    const h = 40;
    const mid = h / 2;
    const step = width / (waveform.length - 1 || 1);
    const svgWidth = width + CLIP_EDGE_PADDING * 2;
    
    let topPath = "";
    let bottomPath = "";
    
    for (let i = 0; i < waveform.length; i++) {
      const x = i * step;
      const v = waveform[i];
      const topY = mid - v * mid * 0.85;
      const bottomY = mid + v * mid * 0.85;
      
      topPath += i === 0 ? `M ${x} ${topY}` : ` L ${x} ${topY}`;
      bottomPath = ` L ${x} ${bottomY}` + bottomPath;
    }
    const pathData = `${topPath}${bottomPath} Z`;

    return (
      <svg width={svgWidth} height={h} viewBox={`-${CLIP_EDGE_PADDING} 0 ${svgWidth} ${h}`} className="clip-waveform-svg">
        <line x1="0" y1={mid} x2={width} y2={mid} stroke={color.waveform} strokeWidth="1" opacity="0.35" />
        <path d={pathData} fill={color.waveform} stroke={color.border} strokeWidth="0.5" opacity="0.8" />
      </svg>
    );
  }, []);

  const renderEnvelope = useCallback((clip, width, height, allClipsList, allowEnvelopeEditing) => {
    const env = allowEnvelopeEditing ? (clip.envelope || DEFAULT_ENVELOPE) : DEFAULT_ENVELOPE;
    const VIS_STEPS = Math.max(100, Math.ceil(width / 2));
    
    const effectivePointsStr = Array.from({ length: VIS_STEPS + 1 }, (_, i) => {
      const tNorm = i / VIS_STEPS;
      const finalGain = getEffectiveGainAtTime(clip, clip.startTime + tNorm * clip.duration, allClipsList, env, allowEnvelopeEditing ? 'manual' : 'automatic');
      return `${tNorm * width},${(1 - finalGain) * height}`;
    }).join(' ');

    const svgWidth = width + CLIP_EDGE_PADDING * 2;

    return (
      <svg className="envelope-svg" width={svgWidth} height={height} viewBox={`-${CLIP_EDGE_PADDING} 0 ${svgWidth} ${height}`}>
        <polygon points={`0,${height} ${effectivePointsStr} ${width},${height}`} fill="var(--clip-accent-bg)" stroke="none" />
        <polyline points={effectivePointsStr} fill="none" stroke="var(--accent)" strokeWidth="1.5" />
        {allowEnvelopeEditing && env.map((wp, i) => (
          <circle
            key={i}
            cx={wp.time * width}
            cy={(1 - wp.gain) * height}
            r="5"
            className="envelope-waypoint"
            onPointerDown={(e) => handleWaypointPointerDown(e, clip, clip._trackId, i)}
            title={`${(wp.gain * 100).toFixed(0)}% at ${(wp.time * 100).toFixed(0)}% (double-tap to remove)`}
          />
        ))}
      </svg>
    );
  }, [handleWaypointPointerDown]);

  const getActiveFade = useCallback((envelope) => {
    if (!envelope || envelope.length < 2) return 'none';
    const first = envelope[0], last = envelope[envelope.length - 1];
    if (first.gain === 1.35 && last.gain === 1) return 'ejective';
    if (first.gain === 0.85 && last.gain === 0.85) return 'sustain';
    if (first.gain === 0.55 && last.gain === 0.55) return 'smooth';
    if (first.gain === 0.35 && last.gain === 0.35) return 'bridge';
    if (first.gain === 0 && last.gain === 0 && envelope.length >= 4) return 'inout';
    if (first.gain === 0 && last.gain >= 0.9) return 'in';
    if (first.gain >= 0.9 && last.gain === 0) return 'out';
    return 'custom';
  }, []);

  const totalDuration = getTotalDuration();
  const timelineWidth = Math.max(totalDuration * zoom, 200);

  const renderOnnxTtsPanel = () => {
    return (
      <div className="onnx-panel">
        <div className="onnx-panel-header">
          <h2 className="onnx-title">Ge'ez TTS (Optimized WebAssembly)</h2>
          <span className="beta-tag">BETA</span>
        </div>
        <p className="onnx-description">
          Synthesizes Ge'ez script using ONNX WebAssembly locally on your device.
        </p>

        <div className="onnx-voice-callout" role="note">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="onnx-callout-icon">
            <path d="M11 5L6 9H2v6h4l5 4V5z"/>
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
          </svg>
          Beta TTS currently supports male voice only.
        </div>

        <button
          className="onnx-generate-btn"
          disabled={onnxLoading || onnxGenerating || !text.trim()}
          onClick={handleGenerateOnnxSpeech}
        >
          {onnxLoading
            ? "Loading Model..."
            : onnxGenerating
            ? "Generating Speech..."
            : !text.trim()
            ? "Enter Text to Generate"
            : "Generate Speech"}
        </button>

        {(onnxLoading || onnxGenerating) && (
          <div className="onnx-progress-container">
            <div
              className="onnx-progress-bar"
              style={{ width: `${onnxProgress}%` }}
            >
              {onnxProgress}%
            </div>
          </div>
        )}

        <div className="onnx-status-container">
          {(onnxLoading || onnxGenerating) && <div className="onnx-spinner" />}
          <div className="onnx-status-text" style={{ color: onnxStatusColor }}>
            {onnxStatus}
          </div>
        </div>

        {onnxFailed && (
          <button
            className="onnx-generate-btn retry"
            onClick={() => setOnnxFailed(false)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
              <path d="M3 3v5h5"/>
              <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/>
              <path d="M16 16h5v5"/>
            </svg>
            Retry Loading Model
          </button>
        )}

        {audioUrl && (
          <div className="onnx-audio-container">
            <span className="onnx-audio-label">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M11 5L6 9H2v6h4l5 4V5z"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
              </svg>
              Local Playback Output:
            </span>
            <audio
              ref={onnxAudioPlayerRef}
              src={audioUrl}
              controls
            />
            
            <button
              className="onnx-download-btn"
              onClick={() => {
                const a = document.createElement('a');
                a.href = audioUrl;
                a.download = `geez-tts-${Date.now()}.wav`;
                a.click();
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Download Synthesized Speech
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="audio-panel">
      {/* Mode Toggle Tabs */}
      <div className="audio-engine-toggle">
        <button
          className={`engine-toggle-btn ${audioMode === 'timeline' ? 'active' : ''}`}
          onClick={() => handleModeToggle('timeline')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="4" y1="21" x2="4" y2="14"/>
            <line x1="4" y1="10" x2="4" y2="3"/>
            <line x1="12" y1="21" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12" y2="3"/>
            <line x1="20" y1="21" x2="20" y2="16"/>
            <line x1="20" y1="12" x2="20" y2="3"/>
            <line x1="2" y1="14" x2="6" y2="14"/>
            <line x1="10" y1="8" x2="14" y2="8"/>
            <line x1="18" y1="16" x2="22" y2="16"/>
          </svg>
          Clip Timeline DAW
        </button>
        <button
          className={`engine-toggle-btn ${audioMode === 'onnx' ? 'active' : ''}`}
          onClick={() => handleModeToggle('onnx')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="4" y="4" width="16" height="16" rx="2" ry="2"/>
            <rect x="9" y="9" width="6" height="6"/>
            <line x1="9" y1="1" x2="9" y2="4"/>
            <line x1="15" y1="1" x2="15" y2="4"/>
            <line x1="9" y1="20" x2="9" y2="23"/>
            <line x1="15" y1="20" x2="15" y2="23"/>
            <line x1="20" y1="9" x2="23" y2="9"/>
            <line x1="20" y1="15" x2="23" y2="15"/>
            <line x1="1" y1="9" x2="4" y2="9"/>
            <line x1="1" y1="15" x2="4" y2="15"/>
          </svg>
          Local Browser TTS <span className="beta-badge">BETA</span>
        </button>
      </div>

      {audioMode === 'onnx' ? (
        renderOnnxTtsPanel()
      ) : (
        <>
          {/* DAW Toolbar */}
          <div className="audio-toolbar" aria-label="Audio editor toolbar">
            <ToolbarGroup label="Transport">
              <button className={`toolbar-btn ${isPlaying ? 'playing' : ''}`} onClick={() => handlePlay()} disabled={totalClips === 0} aria-label={isPlaying ? 'Pause playback' : 'Play audio timeline'}>
                {isPlaying ? (
                  <>
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="6" y="4" width="4" height="16"/>
                      <rect x="14" y="4" width="4" height="16"/>
                    </svg>
                    Pause
                  </>
                ) : (
                  <>
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polygon points="5 3 19 12 5 21 5 3"/>
                    </svg>
                    Play
                  </>
                )}
              </button>
              <button className="toolbar-btn" onClick={handleStop} disabled={!isPlaying && playheadPos === 0} aria-label="Stop playback and return to the start">
                <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="4" y="4" width="16" height="16" rx="1"/>
                </svg>
                Stop
              </button>
              <button className="toolbar-btn export-btn" onClick={handleExportMix} disabled={totalClips === 0 || isExporting} aria-label="Export the mixed audio timeline">
                {isExporting ? (
                  <>
                    <svg className="spin-animation" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="12" y1="2" x2="12" y2="6"/>
                      <line x1="12" y1="18" x2="12" y2="22"/>
                      <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/>
                      <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
                      <line x1="2" y1="12" x2="6" y2="12"/>
                      <line x1="18" y1="12" x2="22" y2="12"/>
                      <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/>
                      <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
                    </svg>
                    Rendering...
                  </>
                ) : (
                  <>
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Export Mix
                  </>
                )}
              </button>
            </ToolbarGroup>

            <ToolbarGroup label="History">
              <button className="toolbar-btn" onClick={handleUndo} title="Undo previous edit (Ctrl+Z)" disabled={!canUndo} aria-label="Undo previous edit">↩</button>
              <button className="toolbar-btn" onClick={handleRedo} title="Redo undone edit (Ctrl+Shift+Z)" disabled={!canRedo} aria-label="Redo undone edit">↪</button>
              <button className="toolbar-btn" onClick={handleReset} disabled={!text} title="Reset text and audio timeline" aria-label="Reset text and audio timeline">
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                  <path d="M3 3v5h5"/>
                  <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/>
                  <path d="M16 16h5v5"/>
                </svg>
                Reset
              </button>
            </ToolbarGroup>

            <ToolbarGroup label="Tracks">
              <button className="toolbar-btn" onClick={handleAddTrack} aria-label="Add a new audio track">
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add Track
              </button>
            </ToolbarGroup>

            <ToolbarGroup label="Timeline">
              <div className="time-display" aria-label="Current timeline position">
                <span className="time-current">{formatTime(playheadPos)}</span>
                <span className="time-sep">/</span>
                <span className="time-total">{formatTime(totalDuration)}</span>
              </div>
            </ToolbarGroup>

            <ToolbarGroup label="Zoom">
              <div className="zoom-controls">
                <button className="zoom-btn" onClick={handleZoomOut} aria-label="Zoom out timeline">−</button>
                <input type="range" min="0" max={ZOOM_LEVELS.length - 1}
                  value={ZOOM_LEVELS.indexOf(zoom) !== -1 ? ZOOM_LEVELS.indexOf(zoom) : 3}
                  onChange={e => setZoom(ZOOM_LEVELS[parseInt(e.target.value)])} className="zoom-slider" aria-label="Timeline zoom" />
                <button className="zoom-btn" onClick={handleZoomIn} aria-label="Zoom in timeline">+</button>
                <button className="zoom-btn zoom-fit" onClick={handleZoomFit} aria-label="Fit timeline to view">⊡</button>
                <span className="zoom-value">{zoom}%</span>
              </div>
            </ToolbarGroup>

            <ToolbarGroup label="Alignment">
              <button className={`toolbar-btn snap-btn ${snapEnabled ? 'active' : ''}`} onClick={() => setSnapEnabled(!snapEnabled)} aria-label="Toggle snapping clips to the timeline grid">⊞ Snap</button>
            </ToolbarGroup>

            <ToolbarGroup label="Fade">
              <button
                className={`toolbar-btn crossfade-mode-btn ${crossfadeMode === 'manual' ? 'manual' : 'automatic'}`}
                onClick={handleCrossfadeModeToggle}
                aria-pressed={crossfadeMode === 'manual'}
                aria-label={crossfadeMode === 'manual' ? 'Manual crossfade mode' : 'Automatic crossfade mode'}
                title="Automatic crossfades overlapping clips; manual shows envelope and clip effect controls"
              >
                {crossfadeMode === 'automatic' ? 'Auto Xfade' : 'Manual Xfade'}
              </button>
            </ToolbarGroup>

            <ToolbarGroup label="Voice">
              <div className="voice-selector-mini">
                <select id="audio-voice-select" value={selectedVoice} onChange={e => setSelectedVoice(e.target.value)} aria-label="Text-to-speech voice">
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </ToolbarGroup>

            <ToolbarGroup label="Master Vol">
              <div className="master-vol">
                <span className="master-vol-label">Vol</span>
                <input type="range" min="0" max="1" step="0.05" value={masterVolume}
                  onChange={e => setMasterVolume(parseFloat(e.target.value))} className="master-vol-slider" aria-label="Master volume" />
                <span className="master-vol-value">{(masterVolume * 100).toFixed(0)}%</span>
              </div>
            </ToolbarGroup>
          </div>

          {/* DAW Status bar */}
          <div className="audio-status">
            <span className="status-text">{status || `Ready — ${text?.length || 0} chars`}</span>
            <span className="status-count">{totalClips} clips · {totalDuration.toFixed(1)}s</span>
          </div>

          {/* DAW Workspace */}
          <div className="workspace" ref={workspaceRef} onPointerLeave={() => setHoveredClipId(null)} onPointerDown={handleWorkspacePointerDown}>
            {/* Removed onClick={handleTimelineClick} from the scroll wrapper below to prevent accidental jumps */}
            <div className="workspace-scroll" ref={workspaceScrollRef}>
              <div className="ruler-row">
                <div className="ruler-spacer" />
                {/* Added onClick event handler here to capture click interactions strictly inside the ruler boundaries */}
                <div 
                  className="timeline-ruler" 
                  style={{ minWidth: `${timelineWidth}px` }}
                  onClick={handleRulerClick}
                >
                  {renderRulerTicks}
                  <div className="playhead" style={{ left: `${playheadPos * zoom}px` }} />
                </div>
              </div>

              {/* DAW Tracks list */}
              {tracks.map((track) => {
                const anySoloed = tracks.some(t => t.soloed);
                const effVol = getEffectiveTrackVolume(track, anySoloed, masterVolume);
                return (
                  <div key={track.id} className={`track-row ${track.muted ? 'muted' : ''} ${track.soloed ? 'soloed' : ''}`}
                    ref={el => { trackDomRefs.current[track.id] = el; }}>
                    <div className="track-control-panel">
                      <div className="track-top-row">
                        <div className="track-name">{track.label}</div>
                        <button className="close-track-btn" onClick={() => handleClearTrack(track.id)} aria-label={`Remove ${track.label}`} title={`Remove ${track.label}`}>×</button>
                      </div>
                      <div className="track-rates">44100Hz Stereo</div>
                      <div className="track-buttons">
                        <button className={`mute-btn ${track.muted ? 'active-mute' : ''}`} onClick={() => handleMuteToggle(track.id)} aria-label={`${track.muted ? 'Unmute' : 'Mute'} ${track.label}`} title={`${track.muted ? 'Unmute' : 'Mute'} ${track.label}`}>M</button>
                        <button className={`solo-btn ${track.soloed ? 'active-solo' : ''}`} onClick={() => handleSoloToggle(track.id)} aria-label={`${track.soloed ? 'Unsolo' : 'Solo'} ${track.label}`} title={`${track.soloed ? 'Unsolo' : 'Solo'} ${track.label}`}>S</button>
                      </div>
                      <div className="track-slider-row">
                        <input type="range" min="0" max="1" step="0.02" value={track.volume}
                          onChange={e => handleVolumeChange(track.id, e.target.value)} className="track-vol-slider" aria-label={`${track.label} volume`} title={`${track.label} volume`} />
                      </div>
                      <div className="track-gain-display"><span className="gain-value">{effVol.toFixed(2)}</span></div>
                    </div>

                    <div className="track-lane" style={{
                      minWidth: `${timelineWidth}px`,
                    }}>
                      {track.clips.map(clip => {
                        const color = CLIP_COLOR_THEME;
                        const leftPx = clip.startTime * zoom;
                        const widthPx = clip.duration * zoom;
                        const clipWithTrack = { ...clip, _trackId: track.id };
                        const activeFade = getActiveFade(clip.envelope);
                        const isSelected = selectedClipIds.has(clip.id);
                        const isDragging = draggingClipIds?.has(clip.id);

                        return (
                          <div key={clip.id} data-clip-id={clip.id}
                            className={`audio-clip ${color.className} ${crossfadeMode === 'automatic' ? 'auto-crossfade' : 'manual-crossfade'} ${isDragging ? 'drag-origin' : ''}`}
                            style={{
                              left: `${leftPx}px`,
                              width: `${widthPx}px`,
                              zIndex: hoveredClipId === clip.id ? 200 : undefined,
                              boxShadow: isSelected ? '0 0 0 2px var(--clip-accent-waveform), 0 0 10px var(--accent-glow)' : undefined,
                              outline: isSelected ? '2px solid var(--accent)' : undefined
                            }}
                            onPointerDown={e => handleClipPointerDown(e, clip, track.id)}
                            onPointerEnter={() => setHoveredClipId(clip.id)}>

                            <div className="clip-resize-left" onPointerDown={e => handleTrimPointerDown(e, clip, track.id, 'left')} />
                            <div className="clip-resize-right" onPointerDown={e => handleTrimPointerDown(e, clip, track.id, 'right')} />

                            <div className="clip-header">
                              <div className="clip-shift-badge">{clip.startTime.toFixed(2)}s</div>
                              <div className="clip-char">{clip.char}</div>
                              <button className="clip-delete-btn"
                                type="button"
                                onClick={e => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDeleteClip(track.id, clip.id);
                                }}
                                aria-label={`Delete clip ${clip.char}`}
                                title={`Delete clip ${clip.char}`}>
                                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDeleteClip(track.id, clip.id);
                                  }}>
                                  <line x1="18" y1="6" x2="6" y2="18"></line>
                                  <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                              </button>
                            </div>

                            <div className="envelope-area" onClick={e => handleEnvelopeClick(e, clip, track.id)}>
                              {renderWaveform(clip.waveform || [], color, widthPx - 12)}
                              {renderEnvelope(clipWithTrack, widthPx - 12, 36, allClipsGlobal, crossfadeMode === 'manual')}
                            </div>

                            {crossfadeMode === 'manual' && (
                              <div className={`clip-effects ${hoveredClipId === clip.id ? 'visible' : ''}`}
                                onPointerEnter={() => setHoveredClipId(clip.id)}
                                onClick={e => e.stopPropagation()}>
                                {['none', 'in', 'out', 'inout', 'smooth', 'ejective', 'sustain', 'bridge'].map(f => (
                                  <button key={f} className={`effect-btn ${activeFade === f ? 'active' : ''}`}
                                    onClick={() => handleSetFade(clip.id, track.id, f)} title={f}>
                                    {f === 'none' ? '−' : f === 'in' ? '↗' : f === 'out' ? '↘' : f === 'inout' ? '⌓' : f === 'smooth' ? '~' : f === 'ejective' ? '▸' : f === 'sustain' ? '▬' : '◂▸'}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {track.clips.length === 0 && <div className="track-empty-hint">Drag clips here</div>}
                      {/* Unconditionally renders the vertical playhead across the track lanes to remain visible while paused. */}
                      <div className="playhead playhead-lane" style={{ left: `${playheadPos * zoom}px` }} />
                    </div>
                  </div>
                );
              })}

              {/* Dedicated mobile horizontal scroll pad */}
              {isMobile && (
                <div className="mobile-scroll-hint">
                  <span>💡</span>
                  <span>Swipe/drag inside this empty area to scroll horizontally or vertically.</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}